import { useEffect, useMemo, useState, type ReactElement } from 'react'
import type {
  Animal,
  BoardUnit,
  CombineLocation,
  Element,
  ShopSlot,
  ShopState,
  Star,
  UnitDef,
} from '@origami/shared'
// Re-export so existing client imports (`from './Shop'`) keep resolving.
export type { BoardUnit, ShopSlot, ShopState, CombineLocation } from '@origami/shared'
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  useDraggable,
  useDroppable,
  useDndContext,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core'
import {
  ELEMENT_INFO,
  ANIMAL_INFO,
  HeartStatImg,
  StrengthStatImg,
  Sparkle,
  TieredCritter,
} from '../design-system'
import { applyCombine, canCombine } from '@origami/shared'
import type { BuffFloater } from './buffFloaters'
import { describeSynergyDetailed } from './synergyData'
import { EvolutionSequence, EVO_HOLD_MS, type EvolutionUnit } from './EvolutionSequence'
import { useIsMobile, useIsNarrowDesktop, useIsFoldOpen, RotatePrompt } from '../ui/useIsMobile'
import { playSfx } from '../sound'
import './Shop.css'

/** State for the EvolutionSequence overlay — set by Shop when a
 *  combine drop succeeds, cleared when the animation completes or
 *  the user clicks-through to skip. Shop owns this so every consumer
 *  (ShopTestScreen, SoloPlayScreen, future multiplayer) gets the
 *  animation for free without duplicating the wiring. */
interface EvoEvent {
  id: number
  sourceA: EvolutionUnit
  sourceB: EvolutionUnit
  result: EvolutionUnit
  tickedDot: 1 | 2 | 3
  starUp: boolean
  durationMs: number
}

/** Duration of the in-place fill-tick effect at the merged slot
 *  (non-star-up combines — see fireEvolution). Also used as the
 *  buff-floater delay so the +N pops as the tick settles. */
const FILL_FX_MS = 900

/** What the DragOverlay's preview should paint while a drag is
 *  active. Two flavors: a shop card (fresh ★1, pre-purchase) or a
 *  board unit (already on the lineup/bench). Captured at drag-start
 *  from active.data.current so the overlay can render without
 *  pulling state. */
type ActiveDrag =
  | { kind: 'shop'; slot: ShopSlot; compact: boolean }
  | { kind: 'board'; unit: BoardUnit; where: 'active' | 'bench'; index: number; compact: boolean }

function toEvolutionUnit(b: BoardUnit): EvolutionUnit {
  return {
    glyph: b.unit.glyph,
    name: b.unit.name,
    element: b.unit.element,
    hp: b.hp,
    atk: b.atk,
    star: b.star,
  }
}

function freshFromSlot(slot: ShopSlot): BoardUnit {
  return {
    unit: slot.unit,
    star: 1,
    fill: 1,
    hp: slot.unit.stats.hp + (slot.hpBuff ?? 0),
    atk: slot.unit.stats.atk + (slot.atkBuff ?? 0),
  }
}

// === Public state shape — types live in @origami/shared so the server
// state machine can share them. Action callbacks below are pure client
// concern. ===

export interface ShopActions {
  onBuy?: (slotIndex: number) => void
  /** Drag-from-shop landing on a specific board slot (place or combine).
   *  When `target` is occupied by a same-unit-same-star copy the server
   *  resolves into a combine; in that case Shop also passes
   *  `opts.floaterDelayMs` so any `on_combine_witnessed` floaters wait
   *  for the EvolutionSequence overlay to finish before animating. */
  onBuyToSlot?: (
    slotIndex: number,
    target: CombineLocation,
    opts?: { floaterDelayMs?: number },
  ) => void
  onSell?: (where: 'active' | 'bench', slotIndex: number) => void
  /** Combine triggers a fullscreen EvolutionSequence overlay; Shop
   *  forwards its duration via `opts.floaterDelayMs` so the store
   *  holds any `on_combine_witnessed` floaters until the overlay
   *  finishes — otherwise they render hidden under the scrim. */
  onCombine?: (
    from: CombineLocation,
    to: CombineLocation,
    opts?: { floaterDelayMs?: number },
  ) => void
  onMove?: (from: CombineLocation, to: CombineLocation) => void
  onReroll?: () => void
  /** Per-card lock toggle — replaces the old whole-shop lock. */
  onToggleSlotLock?: (slotIndex: number) => void
  onReady?: () => void
  /** Leave the shop and return to the title. Optional so test screens
   *  that don't need it can omit; the production SoloPlayScreen wires
   *  it on the live shop. The handler doesn't abandon the run — the
   *  saved session stays alive and resumable from the title. */
  onExit?: () => void
  /** Open the first-time shop tutorial. Wired by the live solo screen
   *  (and the shop test harness); omitted elsewhere, in which case the
   *  "?" help button is not rendered. */
  onOpenTutorial?: () => void
}

interface ShopProps {
  state: ShopState
  actions?: ShopActions
  /** Multiplayer: surface the local player's current ready bit so the
   *  Ready button can flip its label + visual state to "UNREADY"
   *  when they've already committed. Defaults to false (the solo
   *  shop never sits in the ready state — /ready advances the round
   *  inline — so it can ignore this). */
  isReady?: boolean
  /** Transient toast notice (e.g. "Not enough gold...") to show. Caller
   *  supplies the message + dismiss handler so the store layer can
   *  manage lifetime. Auto-dismisses ~3s after first render. */
  notice?: { message: string; at: number } | null
  onClearNotice?: () => void
  /** Floating HP/ATK delta animations queued by recent shop actions
   *  (on_purchase / on_combine_witnessed / sage_owl on_shop_round_pass).
   *  Shop filters per-slot and forwards the matching entries to each
   *  BoardSlot for rendering. */
  buffFloaters?: BuffFloater[]
  /** Called when a floater's CSS animation finishes so the store can
   *  evict it from its queue. */
  onClearFloater?: (id: string) => void
}

// === Component ===

export function Shop({
  state,
  actions: rawActions = {},
  isReady = false,
  notice = null,
  onClearNotice,
  buffFloaters = [],
  onClearFloater,
}: ShopProps): ReactElement {
  // Sound-augmented action proxy — fires the matching sfx at the
  // gesture (before the server round-trip) so feedback is instant,
  // then delegates. Covers all three layout trees (desktop / mobile /
  // fold) from one place. Combine audio is owned by the ceremony /
  // fill-tick fx, NOT here, to avoid doubling.
  const actions = useMemo<ShopActions>(
    () => ({
      ...rawActions,
      onBuy: rawActions.onBuy && ((i) => { playSfx('shop-buy'); rawActions.onBuy!(i) }),
      onBuyToSlot:
        rawActions.onBuyToSlot &&
        ((i, t, o) => { playSfx('shop-buy'); rawActions.onBuyToSlot!(i, t, o) }),
      onSell:
        rawActions.onSell &&
        ((w, i) => { playSfx('shop-sell'); rawActions.onSell!(w, i) }),
      onReroll: rawActions.onReroll && (() => { playSfx('shop-reroll'); rawActions.onReroll!() }),
      onToggleSlotLock:
        rawActions.onToggleSlotLock &&
        ((i) => { playSfx('shop-lock'); rawActions.onToggleSlotLock!(i) }),
      onReady: rawActions.onReady && (() => { playSfx('shop-ready'); rawActions.onReady!() }),
      onMove:
        rawActions.onMove &&
        ((f, t) => { playSfx('ui-place'); rawActions.onMove!(f, t) }),
      // onCombine: ceremony / fill-fx owns the audio — delegate silent.
    }),
    [rawActions],
  )
  const isMobile = useIsMobile()
  const isNarrowDesktop = useIsNarrowDesktop()
  const foldOpen = useIsFoldOpen()
  const synergyCounts = computeSynergyCounts(state)
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor),
  )

  // Click-to-select for shop cards. Drag bypasses this (drag-to-buy
  // resolves immediately via DnD). Selection clears when the slot
  // becomes bought / vanishes, OR when the user clicks anywhere
  // outside a shop card and its action buttons (Buy / Lock).
  const [selectedSlotIndex, setSelectedSlotIndex] = useState<number | null>(null)

  // Inline toast for client-side rejections that never hit the server
  // (e.g. drag-to-buy with insufficient gold — we short-circuit so the
  // eager combine animation doesn't lie about the result). Separate
  // peer to the prop-driven `notice` so store-layer failures (server
  // returned insufficient_gold via click-buy) and local pre-checks
  // both render through the same toast surface. Whichever is freshest
  // (highest `at`) wins.
  const [localNotice, setLocalNoticeRaw] = useState<{ message: string; at: number } | null>(null)
  const setLocalNotice = (message: string) =>
    setLocalNoticeRaw({ message, at: Date.now() })
  const effectiveNotice =
    notice && (!localNotice || (notice.at ?? 0) >= localNotice.at)
      ? notice
      : localNotice
  function clearEffectiveNotice() {
    setLocalNoticeRaw(null)
    onClearNotice?.()
  }
  // Click-to-select for board (lineup + bench) cards. Reveals the
  // contextual 質札 pawn-ticket sell button above the board.
  // Mutually exclusive with shop selection: selecting one clears the
  // other (single-focus model, see effects below).
  const [selectedBoardLoc, setSelectedBoardLoc] = useState<CombineLocation | null>(null)
  // Active drag preview state. Tracks what's being dragged so the
  // DragOverlay can render a single pre-composited clone under the
  // pointer. The source element stays put (with a dim class); only
  // the lightweight clone moves, so heavy painted card chrome
  // (filter:drop-shadow on a 600×900 bitmap frame) doesn't
  // re-rasterize every frame.
  const [activeDrag, setActiveDrag] = useState<ActiveDrag | null>(null)
  // Combine animation overlay state. Bumped via incrementing id so a
  // back-to-back combine cleanly remounts the animation component
  // (the consumer's onCombine handler updates server state async; the
  // local animation kicks off immediately).
  const [evoEvent, setEvoEvent] = useState<EvoEvent | null>(null)
  const [evoCounter, setEvoCounter] = useState(0)
  // In-place fill-tick effect at the merged slot (non-star-up
  // combines skip the fullscreen ceremony).
  const [fillFx, setFillFx] = useState<{
    id: number
    loc: CombineLocation
    tickedDot: 1 | 2 | 3
  } | null>(null)
  useEffect(() => {
    if (!fillFx) return
    const t = setTimeout(() => setFillFx(null), FILL_FX_MS + 200)
    return () => clearTimeout(t)
  }, [fillFx])
  /** fillFx for a given board location, if one is playing there. */
  function fillFxFor(kind: 'active' | 'bench', index: number) {
    return fillFx && fillFx.loc.kind === kind && fillFx.loc.index === index
      ? { id: fillFx.id, tickedDot: fillFx.tickedDot }
      : null
  }
  /** Kick off the combine overlay. Returns the durationMs so the
   *  caller can forward it to the store action as `floaterDelayMs`
   *  (holds `on_combine_witnessed` floaters under wraps until the
   *  overlay finishes). Returns 0 when no overlay fired. */
  function fireEvolution(
    source: BoardUnit,
    target: BoardUnit,
    targetLoc?: CombineLocation,
  ): number {
    if (canCombine(source, target) !== true) return 0
    let combine
    try {
      combine = applyCombine(source, target)
    } catch {
      return 0
    }
    const id = evoCounter + 1
    setEvoCounter(id)
    // Star-ups get the fullscreen ceremony; plain fill ticks play a
    // short in-place effect at the merged slot instead — combining is
    // frequent, and a modal for every +fill is friction, not reward.
    if (!combine.starUp) {
      playSfx('combine-tick')
      if (targetLoc) {
        setFillFx({ id, loc: targetLoc, tickedDot: combine.tickedDot })
      }
      return FILL_FX_MS
    }
    const durationMs = 3200
    setEvoEvent({
      id,
      sourceA: toEvolutionUnit(source),
      sourceB: toEvolutionUnit(target),
      result: toEvolutionUnit(combine.result),
      tickedDot: combine.tickedDot,
      starUp: combine.starUp,
      durationMs,
    })
    // Hold board floaters until the overlay (animation + lingering
    // hold) fully clears, so they don't animate hidden under the scrim.
    return durationMs + EVO_HOLD_MS
  }
  useEffect(() => {
    if (selectedSlotIndex === null) return
    const slot = state.shopSlots[selectedSlotIndex]
    if (!slot || slot.bought) {
      setSelectedSlotIndex(null)
    }
  }, [state.shopSlots, selectedSlotIndex])
  useEffect(() => {
    if (selectedSlotIndex === null) return
    function handleClickOutside(e: MouseEvent): void {
      const target = e.target as HTMLElement | null
      if (!target) return
      // Stay selected when clicking inside any shop card or the
      // drafting bar's action buttons (which fire their own handlers).
      if (target.closest('.shop-card')) return
      if (target.closest('.shop-drafting__buttons')) return
      setSelectedSlotIndex(null)
    }
    document.addEventListener('click', handleClickOutside)
    return () => document.removeEventListener('click', handleClickOutside)
  }, [selectedSlotIndex])

  // Board-selection effects: clear when the unit vanishes (sold,
  // combined, etc.), and clear on click outside a board slot or the
  // sell-ticket button.
  useEffect(() => {
    if (!selectedBoardLoc) return
    const arr =
      selectedBoardLoc.kind === 'active' ? state.activeLine : state.bench
    if (!arr[selectedBoardLoc.index]) setSelectedBoardLoc(null)
  }, [state.activeLine, state.bench, selectedBoardLoc])
  useEffect(() => {
    if (!selectedBoardLoc) return
    function handleClickOutside(e: MouseEvent): void {
      const target = e.target as HTMLElement | null
      if (!target) return
      if (target.closest('.board-slot')) return
      if (target.closest('.sell-ticket')) return
      setSelectedBoardLoc(null)
    }
    document.addEventListener('click', handleClickOutside)
    return () => document.removeEventListener('click', handleClickOutside)
  }, [selectedBoardLoc])

  // Keyboard: Esc deselects everything; Enter on a selected board
  // unit commits the sell.
  useEffect(() => {
    function handleKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') {
        setSelectedSlotIndex(null)
        setSelectedBoardLoc(null)
        return
      }
      if (e.key === 'Enter' && selectedBoardLoc) {
        actions.onSell?.(selectedBoardLoc.kind, selectedBoardLoc.index)
        setSelectedBoardLoc(null)
      }
    }
    document.addEventListener('keydown', handleKey)
    return () => document.removeEventListener('keydown', handleKey)
  }, [selectedBoardLoc, actions])

  const selectedSlot =
    selectedSlotIndex !== null ? state.shopSlots[selectedSlotIndex] : null
  const selectedBoardUnit =
    selectedBoardLoc !== null
      ? (selectedBoardLoc.kind === 'active'
          ? state.activeLine[selectedBoardLoc.index]
          : state.bench[selectedBoardLoc.index]) ?? null
      : null

  /** Tap-to-place: mobile counterpart to drag-and-drop. When something
   *  is selected and the user taps a target slot, resolve the action
   *  the same way handleDragEnd would. Returns true on a successful
   *  placement so callers can suppress the default toggle-selection. */
  function tryTapPlace(target: CombineLocation): boolean {
    // Shop card selected → buy into target slot. If the target is
    // occupied by a same-unit-same-star, the server resolves into a
    // combine; mirror the desktop drag path's local EvolutionSequence
    // pre-fire so the animation kicks off immediately.
    if (selectedSlotIndex !== null) {
      const slot = state.shopSlots[selectedSlotIndex]
      const targetUnit = locUnit(state, target)
      let floaterDelayMs = 0
      if (slot && targetUnit) {
        floaterDelayMs = fireEvolution(freshFromSlot(slot), targetUnit, target)
      }
      if (slot) {
        actions.onBuyToSlot?.(
          selectedSlotIndex,
          target,
          floaterDelayMs > 0 ? { floaterDelayMs } : undefined,
        )
      }
      setSelectedSlotIndex(null)
      return true
    }
    // Board unit selected → resolve as combine / move / swap (the
    // mismatched-units path falls through to onMove which the server
    // handles as a swap-with-occupied, same as handleDragEnd).
    if (selectedBoardLoc) {
      if (
        selectedBoardLoc.kind === target.kind &&
        selectedBoardLoc.index === target.index
      ) {
        return false
      }
      const sourceUnit = locUnit(state, selectedBoardLoc)
      const targetUnit = locUnit(state, target)
      if (sourceUnit && targetUnit) {
        if (canCombine(sourceUnit, targetUnit) === true) {
          const floaterDelayMs = fireEvolution(sourceUnit, targetUnit, target)
          actions.onCombine?.(
            selectedBoardLoc,
            target,
            floaterDelayMs > 0 ? { floaterDelayMs } : undefined,
          )
        } else {
          actions.onMove?.(selectedBoardLoc, target)
        }
      } else if (sourceUnit && !targetUnit) {
        actions.onMove?.(selectedBoardLoc, target)
      }
      setSelectedBoardLoc(null)
      return true
    }
    return false
  }

  function handleDragStart(e: DragStartEvent): void {
    playSfx('ui-pickup')
    const data = e.active.data.current as
      | {
          kind?: 'shop'
          slot?: number
          slotUnit?: UnitDef
          unit?: BoardUnit
          where?: 'active' | 'bench'
          index?: number
        }
      | undefined
    if (!data) return
    if (data.kind === 'shop' && typeof data.slot === 'number') {
      const slot = state.shopSlots[data.slot]
      if (slot) setActiveDrag({ kind: 'shop', slot, compact: isMobile || foldOpen })
    } else if (data.unit && data.where && typeof data.index === 'number') {
      setActiveDrag({
        kind: 'board',
        unit: data.unit,
        where: data.where,
        index: data.index,
        compact: isMobile || foldOpen,
      })
    }
  }

  function handleDragCancel(): void {
    setActiveDrag(null)
  }

  function handleDragEnd(e: DragEndEvent): void {
    setActiveDrag(null)
    const { active, over } = e
    if (!over || active.id === over.id) return
    const fromLoc = parseLoc(String(active.id))
    if (!fromLoc) return

    // Shop → board purchase (drag from shop card to a board slot).
    if (typeof fromLoc === 'object' && fromLoc.kind === 'shop') {
      const toLoc = parseLoc(String(over.id))
      if (!toLoc || (typeof toLoc === 'object' && toLoc.kind === 'shop')) {
        return
      }
      const slot = state.shopSlots[fromLoc.slot]
      if (!slot) return
      // Affordability pre-check — without this, an insufficient_gold
      // buy still played the eager EvolutionSequence (synthesized
      // ★1 + targetUnit), making the user think the combine
      // succeeded when the server quietly rejected the purchase.
      // Skip animation + skip the server call; surface a toast.
      const cost = slot.isBonus ? 0 : slot.cost
      if (state.gold < cost) {
        setLocalNotice(`Not enough gold — needs ${cost}◈ (you have ${state.gold}◈).`)
        return
      }
      // If the target is occupied by a same-unit-same-star, this is a
      // buy-and-combine: fire the EvolutionSequence overlay locally
      // against a synthesized fresh ★1 BoardUnit. The consumer's
      // onBuyToSlot handler (e.g. soloStore.buyToSlot) is expected to
      // sequence /buy → /combine on the server side.
      const targetUnit = locUnit(state, toLoc)
      let floaterDelayMs = 0
      if (targetUnit) {
        floaterDelayMs = fireEvolution(freshFromSlot(slot), targetUnit, toLoc)
      }
      actions.onBuyToSlot?.(
        fromLoc.slot,
        toLoc,
        floaterDelayMs > 0 ? { floaterDelayMs } : undefined,
      )
      return
    }

    const toLoc = parseLoc(String(over.id))
    if (!toLoc || (typeof toLoc === 'object' && toLoc.kind === 'shop')) return

    const sourceUnit = (active.data.current as { unit?: BoardUnit } | undefined)
      ?.unit
    const targetUnit = locUnit(state, toLoc)

    if (sourceUnit && targetUnit) {
      if (canCombine(sourceUnit, targetUnit) === true) {
        const floaterDelayMs = fireEvolution(sourceUnit, targetUnit, toLoc)
        actions.onCombine?.(
          fromLoc,
          toLoc,
          floaterDelayMs > 0 ? { floaterDelayMs } : undefined,
        )
      } else {
        // Two board units that can't combine — swap them instead of
        // rejecting the gesture. The player almost always intended a
        // reorder; a silent no-op feels broken, and a "same unit only"
        // error feels punishing for what's a natural rearrange action.
        // The server's reorder endpoint handles swap-with-occupied
        // alongside move-into-empty in the same call.
        actions.onMove?.(fromLoc, toLoc)
      }
    } else if (sourceUnit && !targetUnit) {
      actions.onMove?.(fromLoc, toLoc)
    }
  }

  return (
    <DndContext
      sensors={sensors}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
    {effectiveNotice && (
      <ShopNoticeToast
        key={effectiveNotice.at}
        message={effectiveNotice.message}
        onDismiss={clearEffectiveNotice}
      />
    )}
    {isMobile && <RotatePrompt />}
    {foldOpen ? (
      <FoldShop
        state={state}
        actions={actions}
        isReady={isReady}
        synergyCounts={synergyCounts}
        selectedSlotIndex={selectedSlotIndex}
        setSelectedSlotIndex={setSelectedSlotIndex}
        selectedBoardLoc={selectedBoardLoc}
        setSelectedBoardLoc={setSelectedBoardLoc}
        selectedSlot={selectedSlot}
        selectedBoardUnit={selectedBoardUnit}
        tryTapPlace={tryTapPlace}
        buffFloaters={buffFloaters}
        onClearFloater={onClearFloater}
        fillFxFor={fillFxFor}
      />
    ) : isMobile ? (
      <MobileShop
        state={state}
        actions={actions}
        isReady={isReady}
        synergyCounts={synergyCounts}
        selectedSlotIndex={selectedSlotIndex}
        setSelectedSlotIndex={setSelectedSlotIndex}
        selectedBoardLoc={selectedBoardLoc}
        setSelectedBoardLoc={setSelectedBoardLoc}
        selectedSlot={selectedSlot}
        selectedBoardUnit={selectedBoardUnit}
        tryTapPlace={tryTapPlace}
        buffFloaters={buffFloaters}
        onClearFloater={onClearFloater}
        fillFxFor={fillFxFor}
      />
    ) : (
    <div className={`paper paper--atelier shop-page ${isNarrowDesktop ? 'shop-page--narrow' : ''}`}>
      <header className="shop-header">
        <div
          className="shop-header__shoten"
          aria-label="shop"
          title="商店 · shop"
        >
          {/* Render the two kanji as separate spans so flex column
              stacks them vertically without relying on
              writing-mode + text-orientation: upright, which doesn't
              hold in our serif fallback chain (renders sideways). */}
          <span aria-hidden>商</span>
          <span aria-hidden>店</span>
        </div>
        {actions.onExit && (
          <button
            type="button"
            className="shop-header__exit"
            onClick={actions.onExit}
            title="Exit to title"
            aria-label="Exit to title"
          >
            <span aria-hidden className="shop-header__exit-arrow">←</span>
            <span className="shop-header__exit-label">Exit</span>
          </button>
        )}
        {actions.onOpenTutorial && (
          <button
            type="button"
            className="shop-header__help"
            onClick={actions.onOpenTutorial}
            title="How to play"
            aria-label="How to play"
          >
            ?
          </button>
        )}
        <div className="shop-header__title">
          <div>
            <div className="shop-header__eyebrow tiny-cap">
              <span>SHOP · ROUND {state.round}</span>
              <span aria-hidden className="shop-header__eyebrow-sep">·</span>
              <span
                className="shop-header__lv"
                aria-label={`shop tier ${state.shopLevel} of 5`}
              >
                TIER {TIER_ROMAN[state.shopLevel]}
                <span className="shop-header__lv-folds" aria-hidden>
                  {[1, 2, 3, 4, 5].map((l) => (
                    <span
                      key={l}
                      className={`shop-header__lv-fold ${l <= state.shopLevel ? 'shop-header__lv-fold--filled' : ''}`}
                    />
                  ))}
                </span>
              </span>
            </div>
            <h1 className="cute-title shop-header__h1">The Folding Room</h1>
          </div>
        </div>
        <div className="shop-header__cta">
          <button
            className={`cute-btn dark pop shop-header__ready${
              isReady ? ' shop-header__ready--on' : ''
            }`}
            onClick={actions.onReady}
            aria-pressed={isReady}
            title={
              isReady
                ? 'You are ready — click to unready and edit your team'
                : 'Lock in your team for the round'
            }
          >
            {isReady ? '↺ UNREADY' : 'READY ✦'}
          </button>
        </div>
        <div className="shop-header__stats">
          <button
            className="shop-header__reroll"
            onClick={actions.onReroll}
            disabled={state.gold < state.rerollCost}
            title={`Reroll the shop · ${state.rerollCost} gold`}
          >
            <span className="shop-header__reroll-glyph" aria-hidden>↻</span>
            <span className="shop-header__reroll-label">Reroll</span>
            <span className="shop-header__reroll-cost" aria-hidden>
              {state.rerollCost}◈
            </span>
          </button>
          <div className="shop-header__stat-stamps">
            <ShopStat
              className="shop-stat--gold"
              icon="◈"
              label="GOLD"
              value={String(state.gold).padStart(2, '0')}
              accent="var(--gold-deep)"
            />
            <ShopStat
              className="shop-stat--health"
              icon="♥"
              label="HEALTH"
              value={String(state.health)}
              sub={`/${state.maxHealth}`}
              accent="var(--sakura-deep)"
            />
            <ShopStat
              className="shop-stat--wins"
              icon="🏆"
              label="WINS"
              value={String(state.wins)}
              sub={`/${state.maxWins}`}
              accent="var(--mint-deep)"
            />
          </div>
        </div>
      </header>

      <div className="shop-content">
        <section className="shop-drafting">
          <div className="shop-drafting__bar">
            <div className="shop-drafting__title">
              <h2 className="cute-title shop-drafting__h">Drafting Table</h2>
              <span className="hand-note">— pick paper friends to fold</span>
            </div>
            {selectedSlot && (
              <div className="shop-drafting__buttons">
                <button
                  className="cute-btn dark"
                  onClick={() => {
                    if (selectedSlotIndex === null) return
                    actions.onBuy?.(selectedSlotIndex)
                    setSelectedSlotIndex(null)
                  }}
                  disabled={
                    !selectedSlot.isBonus &&
                    state.gold < selectedSlot.cost
                  }
                >
                  ✦ Buy {selectedSlot.unit.name} ·{' '}
                  <b style={{ color: 'var(--gold-deep)' }}>
                    {selectedSlot.isBonus ? 'free' : `${selectedSlot.cost}◈`}
                  </b>
                </button>
                {/* Lock is meaningless on the FREE bonus slot — that
                    extra card only exists for the current shop view
                    and is replaced next reroll regardless. Hide the
                    toggle so the player isn't offered a no-op. */}
                {!selectedSlot.isBonus && (
                  <button
                    className={`cute-btn ${selectedSlot.locked ? 'gold' : 'cream'}`}
                    onClick={() => {
                      if (selectedSlotIndex === null) return
                      actions.onToggleSlotLock?.(selectedSlotIndex)
                    }}
                  >
                    {selectedSlot.locked ? '🔒 Locked' : '🔓 Lock'}
                  </button>
                )}
              </div>
            )}
          </div>
          {(() => {
            const indexed = state.shopSlots.map((slot, i) => ({ slot, i }))
            const baseEntries = indexed.filter((x) => !x.slot.isBonus)
            const bonusEntries = indexed.filter((x) => x.slot.isBonus)
            // All deployed units (active line + bench) — used to flag
            // shop cards that would combine if purchased so the bob
            // animation only fires on cards with a board match.
            const allBoardUnits = [...state.activeLine, ...state.bench]
            const hasCombineMatch = (slot: ShopSlot): boolean => {
              if (slot.bought) return false
              const synthetic: BoardUnit = {
                unit: slot.unit,
                star: 1,
                fill: 1,
                hp: slot.unit.stats.hp,
                atk: slot.unit.stats.atk,
              }
              return allBoardUnits.some(
                (u) => u !== null && canCombine(synthetic, u) === true,
              )
            }
            const renderCard = ({ slot, i }: { slot: ShopSlot; i: number }): ReactElement => (
              <ShopCard
                key={i}
                slot={slot}
                slotIndex={i}
                selected={selectedSlotIndex === i}
                hasCombineMatch={hasCombineMatch(slot)}
                onSelect={() => {
                  setSelectedBoardLoc(null)
                  setSelectedSlotIndex(i === selectedSlotIndex ? null : i)
                }}
                onDoubleBuy={() => {
                  actions.onBuy?.(i)
                  setSelectedSlotIndex(null)
                }}
                onContextLock={() => actions.onToggleSlotLock?.(i)}
              />
            )
            return (
              <div className="shop-grid">
                <div className="shop-grid__base">
                  {baseEntries.map(renderCard)}
                </div>
                {bonusEntries.length > 0 && (
                  <div className="shop-grid__bonus">
                    {bonusEntries.map(renderCard)}
                  </div>
                )}
              </div>
            )
          })()}
        </section>

        <section className="shop-board-section">
          <div className="shop-board-heading">
            <h2 className="cute-title shop-board-heading__h">Your Folded Five</h2>
            <span className="hand-note">⟵ rightmost strikes first</span>
          </div>
          <div className="shop-board">
            <div className="shop-board__col shop-board__col--active">
              <div className="shop-board__rack-label">LINEUP ✦</div>
              <div className="shop-line">
                {state.activeLine
                  .map((unit, i) => ({ unit, i }))
                  .reverse()
                  .map(({ unit, i }) => (
                    <BoardSlot
                      key={i}
                      unit={unit}
                      index={i + 1}
                      where="active"
                      selected={
                        selectedBoardLoc?.kind === 'active' &&
                        selectedBoardLoc.index === i
                      }
                      floaters={buffFloaters.filter(
                        (f) => f.where === 'active' && f.position === i,
                      )}
                      onFloaterDone={onClearFloater}
                      fillFx={fillFxFor('active', i)}
                      onSelect={() => {
                        setSelectedSlotIndex(null)
                        setSelectedBoardLoc((prev) =>
                          prev?.kind === 'active' && prev.index === i
                            ? null
                            : { kind: 'active', index: i },
                        )
                      }}
                    />
                  ))}
              </div>
              <div className="shop-board__col-h">
                <span className="tiny-cap">BACK</span>
                <span className="tiny-cap">FRONT</span>
              </div>
            </div>
            <div className="shop-board__bench">
              <div className="shop-board__rack-label">BENCH 💤</div>
              <div className="shop-line shop-line--bench">
                {state.bench.map((unit, i) => (
                  <BoardSlot
                    key={i}
                    unit={unit}
                    index={i + 1}
                    where="bench"
                    selected={
                      selectedBoardLoc?.kind === 'bench' &&
                      selectedBoardLoc.index === i
                    }
                    floaters={buffFloaters.filter(
                      (f) => f.where === 'bench' && f.position === i,
                    )}
                    onFloaterDone={onClearFloater}
                    fillFx={fillFxFor('bench', i)}
                    onSelect={() => {
                      setSelectedSlotIndex(null)
                      setSelectedBoardLoc((prev) =>
                        prev?.kind === 'bench' && prev.index === i
                          ? null
                          : { kind: 'bench', index: i },
                      )
                    }}
                  />
                ))}
              </div>
            </div>
          </div>
        </section>
      </div>

      {isNarrowDesktop ? (
        <SynergyTray counts={synergyCounts} />
      ) : (
      <aside className="shop-synergies">
        <h3 className="cute-title shop-synergies__h">
          <Sparkle size={16} /> Synergies
        </h3>
        <div className="shop-synergies__group-label">ELEMENT</div>
        {(() => {
          const achieved = (
            ['fire', 'water', 'wind', 'wood', 'earth'] as const
          ).filter((el) => (synergyCounts.element[el] ?? 0) >= 2)
          if (achieved.length === 0) {
            return (
              <p className="shop-synergies__empty hand-note">none yet</p>
            )
          }
          return achieved.map((el) => (
            <SynergyRow
              key={el}
              kind="element"
              id={el}
              count={synergyCounts.element[el] ?? 0}
            />
          ))
        })()}
        <div className="shop-synergies__group-label">ANIMAL</div>
        {(() => {
          const achieved = (
            ['bird', 'mammal', 'reptile', 'fish', 'insect'] as const
          ).filter((an) => (synergyCounts.animal[an] ?? 0) >= 2)
          if (achieved.length === 0) {
            return (
              <p className="shop-synergies__empty hand-note">none yet</p>
            )
          }
          return achieved.map((an) => (
            <SynergyRow
              key={an}
              kind="animal"
              id={an}
              count={synergyCounts.animal[an] ?? 0}
            />
          ))
        })()}
        <p className="hand-note shop-synergies__note">
          counts active 5 only ✦ thresholds 2 / 3 / 5 ✦ applied at battle start
        </p>
      </aside>
      )}
    </div>
    )}
    {evoEvent && (
      <div
        className="shop-evo-overlay"
        role="button"
        tabIndex={0}
        aria-label="Skip combine animation"
        onClick={() => setEvoEvent(null)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ' || e.key === 'Escape') {
            setEvoEvent(null)
          }
        }}
      >
        <div style={{ width: 'min(900px, 96vw)' }}>
          <EvolutionSequence
            key={evoEvent.id}
            sourceA={evoEvent.sourceA}
            sourceB={evoEvent.sourceB}
            result={evoEvent.result}
            tickedDot={evoEvent.tickedDot}
            starUp={evoEvent.starUp}
            durationMs={evoEvent.durationMs}
            onComplete={() => setEvoEvent(null)}
          />
        </div>
      </div>
    )}
    {/* Desktop sell-ticket — fixed on the right edge, slides in on
        selection. Mounted at the DndContext root (outside .shop-page
        and .shop-board-section) so it isn't trapped inside any
        position:relative+z-index stacking context that would let the
        narrow-desktop synergies tray (z:30) paint over it. */}
    {!isMobile && !foldOpen && (
      <SellTicket
        unit={selectedBoardUnit}
        onSell={() => {
          if (!selectedBoardLoc) return
          actions.onSell?.(selectedBoardLoc.kind, selectedBoardLoc.index)
          setSelectedBoardLoc(null)
        }}
      />
    )}
    <DragOverlay
      dropAnimation={null}
      style={{ pointerEvents: 'none' }}
    >
      {activeDrag ? <DragPreview drag={activeDrag} /> : null}
    </DragOverlay>
    </DndContext>
  )
}

// === DragOverlay preview ===
//
// Renders a lightweight, non-interactive clone of the dragged
// card/slot inside @dnd-kit's <DragOverlay> portal. Why: the
// dragged source element carries heavy paint cost (the painted
// frame webp + filter:drop-shadow on its ::before pseudo). When
// the source itself translates under the pointer (the default
// dnd-kit setup with `draggable.transform`), the browser
// re-rasterizes that drop-shadow every frame — visible jank.
//
// With DragOverlay: the source stays put (just dims via the
// `--dragging` class), and only this lightweight preview moves.
// dnd-kit positions the overlay element via a transform on its
// container — a single composited GPU layer — so frame cost is
// constant regardless of card chrome complexity.
function DragPreview({ drag }: { drag: ActiveDrag }): ReactElement {
  if (drag.kind === 'shop') {
    return <ShopCardPreview slot={drag.slot} compact={drag.compact} />
  }
  return (
    <BoardUnitPreview
      unit={drag.unit}
      where={drag.where}
      index={drag.index}
      compact={drag.compact}
    />
  )
}

function ShopCardPreview({ slot, compact }: { slot: ShopSlot; compact: boolean }): ReactElement {
  const { unit, cost, isBonus, hpBuff, atkBuff } = slot
  const displayHp = unit.stats.hp + (hpBuff ?? 0)
  const displayAtk = unit.stats.atk + (atkBuff ?? 0)
  const animalInfo = ANIMAL_INFO[unit.animal]
  return (
    <article
      className={[
        'shop-card shop-card--forged shop-card--preview',
        `shop-card--el-${unit.element}`,
        isBonus && 'shop-card--bonus',
        compact && 'shop-card--compact',
      ]
        .filter(Boolean)
        .join(' ')}
      style={{ ['--frame-url' as never]: `url(${forgedFrameUrl(unit.element, 1)})` }}
    >
      <span
        className="shop-card__cost-flag"
        aria-hidden
      >
        {isBonus ? '0 ✦' : `${cost} ◈`}
      </span>
      <div className="shop-card__forge-name" aria-hidden>{unit.name}</div>
      <div className="shop-card__forge-tier" aria-hidden>{TIER_ROMAN[unit.tier]}</div>
      <div className="shop-card__forge-art">
        <TieredCritter glyph={unit.glyph} star={1} size={compact ? 50 : 108} unitId={unit.id} />
      </div>
      <span className="shop-card__forge-hp" aria-hidden>{displayHp}</span>
      <span className="shop-card__forge-atk" aria-hidden>{displayAtk}</span>
      <div
        className="shop-card__forge-chip"
        style={{ background: animalInfo.bg }}
        aria-hidden
      >
        <span
          className="shop-card__forge-chip-kanji"
          style={{ color: animalInfo.accent }}
        >
          {animalInfo.kanji}
        </span>
        <span className="shop-card__forge-chip-label">{animalInfo.en}</span>
      </div>
    </article>
  )
}

function BoardUnitPreview({
  unit,
  where,
  compact,
}: {
  unit: BoardUnit
  where: 'active' | 'bench'
  index: number
  compact: boolean
}): ReactElement {
  const capped = unit.star === 3 && unit.fill === 3
  return (
    <div
      className={[
        'board-slot board-slot--forged board-slot--preview',
        `board-slot--el-${unit.unit.element}`,
        compact && 'board-slot--compact',
      ]
        .filter(Boolean)
        .join(' ')}
      style={{ ['--frame-url' as never]: `url(${forgedFrameUrl(unit.unit.element, unit.star)})` }}
    >
      <div className="board-slot__forge-stars" aria-hidden>{'✦'.repeat(unit.star)}</div>
      <div className="board-slot__forge-tier" aria-hidden>{TIER_ROMAN[unit.unit.tier]}</div>
      <div className="board-slot__forge-art">
        {/* Bench renders without the bob animation; preview echoes
            that for visual fidelity to the source slot. */}
        {where === 'bench' ? (
          <div>
            <TieredCritter glyph={unit.unit.glyph} star={unit.star} size={compact ? 44 : 88} unitId={unit.unit.id} />
          </div>
        ) : (
          <div>
            <TieredCritter glyph={unit.unit.glyph} star={unit.star} size={compact ? 44 : 88} unitId={unit.unit.id} />
          </div>
        )}
      </div>
      <span
        className={[
          'board-slot__forge-hp',
          (unit.accruedBench?.hp ?? 0) > 0 && 'board-slot__forge-hp--accrued',
        ]
          .filter(Boolean)
          .join(' ')}
        aria-hidden
      >
        {unit.hp}
      </span>
      <span
        className={[
          'board-slot__forge-atk',
          (unit.accruedBench?.atk ?? 0) > 0 && 'board-slot__forge-atk--accrued',
        ]
          .filter(Boolean)
          .join(' ')}
        aria-hidden
      >
        {unit.atk}
      </span>
      <div className="board-slot__forge-fill" aria-hidden>
        {[1, 2, 3].map((i) => {
          const filled = i <= unit.fill
          return (
            <span
              key={i}
              className={[
                'board-slot__forge-pip',
                filled && 'board-slot__forge-pip--filled',
                filled && capped && 'board-slot__forge-pip--capped',
              ]
                .filter(Boolean)
                .join(' ')}
            />
          )
        })}
      </div>
    </div>
  )
}

// === Drag-drop helpers ===

type ShopLoc = { kind: 'shop'; slot: number }
type ParsedLoc = CombineLocation | ShopLoc | null

function parseLoc(id: string): ParsedLoc {
  const shopM = id.match(/^shop:slot:(\d+)$/)
  if (shopM) return { kind: 'shop', slot: Number(shopM[1]) }
  const boardM = id.match(/^board:(active|bench):(\d+)$/)
  if (!boardM) return null
  return { kind: boardM[1] as 'active' | 'bench', index: Number(boardM[2]) }
}

function locUnit(state: ShopState, loc: CombineLocation): BoardUnit | null {
  return loc.kind === 'active'
    ? state.activeLine[loc.index] ?? null
    : state.bench[loc.index] ?? null
}

// === Sub-components ===

interface ShopStatProps {
  icon: string
  label: string
  value: string
  sub?: string
  accent: string
  className?: string
}

function ShopStat({
  icon,
  label,
  value,
  sub,
  accent,
  className = '',
}: ShopStatProps): ReactElement {
  return (
    <div
      className={`shop-stat ${className}`.trim()}
      style={{ ['--stamp-accent' as string]: accent }}
    >
      <span className="shop-stat__icon" aria-hidden>
        {icon}
      </span>
      <span className="shop-stat__value">
        {value}
        {sub && <span className="shop-stat__sub">{sub}</span>}
      </span>
      <span className="shop-stat__label">{label}</span>
    </div>
  )
}

/** Small inline vermillion wax-seal stamp with a pale sakura flower.
 *  Originally lived in the shop banner as the title-block avatar.
 *  Now retired from the banner (May 2026) but kept exported for
 *  reuse — appears in the design gallery and is available for
 *  future surfaces (battle-result screens, player profile, etc.).
 *  Echoes the wax-seal dot motif already on .unit-tooltip::before. */
export function SakuraSeal(): ReactElement {
  return (
    <svg
      viewBox="0 0 44 44"
      width="30"
      height="30"
      aria-hidden="true"
      style={{ display: 'block' }}
    >
      <g transform="translate(22 22)" fill="#fffaee">
        {[0, 72, 144, 216, 288].map((deg) => (
          <ellipse
            key={deg}
            cx="0"
            cy="-9"
            rx="3.6"
            ry="5.8"
            transform={`rotate(${deg})`}
          />
        ))}
        <circle r="2.6" fill="var(--gold-deep)" />
      </g>
    </svg>
  )
}

interface ShopCardProps {
  slot: ShopSlot
  slotIndex: number
  selected?: boolean
  /** True when a unit on the active line / bench can combine with
   *  this shop card if purchased. Drives the glyph's bob animation
   *  as a passive "buy me to combine" cue; without a board match,
   *  the glyph sits still so a row of un-actionable cards doesn't
   *  visually buzz. */
  hasCombineMatch?: boolean
  onSelect?: () => void
  /** Double-click: auto-buy into the first empty slot (test screen
   *  resolves bench-then-active; no-op if nothing's open or gold is
   *  short). */
  onDoubleBuy?: () => void
  /** Right-click: toggle this slot's lock. Suppresses the browser
   *  context menu when fired on a shop card. */
  onContextLock?: () => void
  /** Mobile-landscape rendering. Inline-style stat images shrink,
   *  glyph downsizes, and `.shop-card--compact` lets CSS trim chrome
   *  the kanji badges already convey. */
  compact?: boolean
}

const TIER_ROMAN: Record<number, string> = {
  1: 'I',
  2: 'II',
  3: 'III',
  4: 'IV',
  5: 'V',
}

function formatTrigger(trigger: string): string {
  return trigger.replace(/_/g, ' ').toUpperCase()
}

/** Map a unit's element + star to the painted forged-frame asset URL.
 *  All five elements ship today (wind / wood / earth / water / fire). */
function forgedFrameUrl(element: Element, star: 1 | 2 | 3): string {
  return `/ui-assets/${element}-card-frame-${star}.webp`
}

function ShopCard({
  slot,
  slotIndex,
  selected = false,
  hasCombineMatch = false,
  onSelect,
  onDoubleBuy,
  onContextLock,
  compact = false,
}: ShopCardProps): ReactElement {
  const { unit, cost, isBonus, bought, locked, hpBuff, atkBuff } = slot
  const draggable = useDraggable({
    id: `shop:slot:${slotIndex}`,
    disabled: bought,
    data: { kind: 'shop', slot: slotIndex, slotUnit: unit },
  })
  if (bought) {
    return <div className="shop-card shop-card--vacant" aria-hidden />
  }
  // Locked-slot buffs (on_shop_round_pass — sage_owl) bake into the
  // BoardUnit at purchase via freshFromSlot; surface them on the card
  // so the player sees why locking pays off. The numeral tints green
  // on each buffed stat in lieu of a separate badge.
  const displayHp = unit.stats.hp + (hpBuff ?? 0)
  const displayAtk = unit.stats.atk + (atkBuff ?? 0)
  const animalInfo = ANIMAL_INFO[unit.animal]
  // Intentionally NOT applying draggable.transform — the DragOverlay
  // portal renders the visual movement on a single composited
  // layer. Translating the source here would also move the heavy
  // ::before frame + filter:drop-shadow every frame, defeating the
  // overlay's purpose.

  // Forged-frame layout: the painted webp carries the entire card
  // chrome (gold trim, banner shelf, heart/fist icons, tier circle).
  // We overlay only the unit-specific values:
  //   • name in the top banner
  //   • tier numeral in the top-right circle
  //   • TieredCritter glyph in the art window
  //   • HP / ATK numbers over the heart / fist icons
  //   • animal chip floating between heart and fist
  // Element identity comes from the painted frame chrome itself
  // (wind/wood/earth motifs), so no kanji-disc overlay is needed.
  // The frame asset is keyed by current star level (★1 = shop = star 1
  // here; combined upgrades change the frame in the lineup). All
  // percentage anchors mirror ForgedCard in the design Gallery —
  // proven to land cleanly on the painted slots at any card width.
  return (
    <article
      ref={draggable.setNodeRef}
      {...draggable.attributes}
      {...draggable.listeners}
      className={[
        'shop-card shop-card--forged',
        `shop-card--el-${unit.element}`,
        isBonus && 'shop-card--bonus',
        draggable.isDragging && 'shop-card--dragging',
        selected && 'shop-card--selected',
        locked && 'shop-card--locked',
        compact && 'shop-card--compact',
      ]
        .filter(Boolean)
        .join(' ')}
      style={{
        ['--frame-url' as never]: `url(${forgedFrameUrl(unit.element, 1)})`,
        zIndex: selected ? 5 : undefined,
      }}
      onClick={() => { playSfx('ui-tap'); onSelect?.() }}
      onDoubleClick={(e) => {
        e.stopPropagation()
        onDoubleBuy?.()
      }}
      onContextMenu={(e) => {
        e.preventDefault()
        // Bonus slot lock is a no-op AND the server rejects it as
        // an unknown slot; ignore the right-click on bonus cards.
        if (isBonus) return
        onContextLock?.()
      }}
      role="button"
      tabIndex={0}
      aria-pressed={selected}
    >
      {locked && (
        <span className="shop-card__lock-flag" aria-label="locked">
          🔒
        </span>
      )}
      <span
        className="shop-card__cost-flag"
        aria-label={
          isBonus
            ? `free reward (tier ${TIER_ROMAN[unit.tier]})`
            : `${cost} gold, tier ${TIER_ROMAN[unit.tier]}`
        }
        title={
          isBonus
            ? `Free · Tier ${TIER_ROMAN[unit.tier]}`
            : `${cost} gold · Tier ${TIER_ROMAN[unit.tier]}`
        }
      >
        {isBonus ? '0 ✦' : `${cost} ◈`}
      </span>

      <div className="shop-card__forge-name" aria-label={`name: ${unit.name}`}>
        {unit.name}
      </div>
      <div
        className="shop-card__forge-tier"
        aria-label={`tier ${TIER_ROMAN[unit.tier]}`}
      >
        {TIER_ROMAN[unit.tier]}
      </div>

      <div className="shop-card__forge-art">
        <div className={hasCombineMatch ? 'bob' : undefined}>
          <TieredCritter glyph={unit.glyph} star={1} size={compact ? 50 : 108} unitId={unit.id} />
        </div>
      </div>

      <span
        className={[
          'shop-card__forge-hp',
          (hpBuff ?? 0) > 0 && 'shop-card__forge-stat--buffed',
        ]
          .filter(Boolean)
          .join(' ')}
        aria-label={`HP ${displayHp}`}
      >
        {displayHp}
      </span>
      <span
        className={[
          'shop-card__forge-atk',
          (atkBuff ?? 0) > 0 && 'shop-card__forge-stat--buffed',
        ]
          .filter(Boolean)
          .join(' ')}
        aria-label={`ATK ${displayAtk}`}
      >
        {displayAtk}
      </span>

      <div
        className="shop-card__forge-chip"
        style={{ background: animalInfo.bg }}
        title={`animal: ${animalInfo.en}`}
        aria-label={`animal: ${animalInfo.en}`}
      >
        <span
          className="shop-card__forge-chip-kanji"
          style={{ color: animalInfo.accent }}
        >
          {animalInfo.kanji}
        </span>
        <span className="shop-card__forge-chip-label">{animalInfo.en}</span>
      </div>

      <UnitTooltip unit={unit} />
    </article>
  )
}

/** Hover-revealed paper note with full unit identity + ability text.
 *  Used by ShopCard (hangs below — `placement="below"`, default) and
 *  by BoardSlot (hangs above — `placement="above"`, since the active
 *  line / bench sit at the bottom of the viewport and a downward
 *  tooltip would clip). */
interface UnitTooltipProps {
  unit: UnitDef
  placement?: 'above' | 'below'
  /** Star tier whose ability is currently active. Shop cards = 1
   *  (pre-purchase); board slots pass the unit's actual `star`. The
   *  matching row is emphasized; higher tiers read as "next up". */
  currentStar?: Star
  /** Gold refund if this unit is sold. Only meaningful for board
   *  units (active line / bench); shop cards omit it. */
  sellPrice?: number
}

function UnitTooltip({
  unit,
  placement = 'below',
  currentStar = 1,
  sellPrice,
}: UnitTooltipProps): ReactElement {
  const elInfo = ELEMENT_INFO[unit.element]
  const animalInfo = ANIMAL_INFO[unit.animal]
  const starSpecs = unit.ability.stars
  const cls = [
    'unit-tooltip',
    placement === 'above' && 'unit-tooltip--above',
    sellPrice !== undefined && 'unit-tooltip--with-sell',
  ]
    .filter(Boolean)
    .join(' ')
  return (
    <div className={cls} role="tooltip">
      {sellPrice !== undefined && (
        <span
          className="unit-tooltip__sell-price"
          aria-label={`sells for ${sellPrice} gold`}
        >
          SELL {sellPrice}◈
        </span>
      )}
      <div className="unit-tooltip__eyebrow">
        <span className="unit-tooltip__eyebrow-main">
          {animalInfo.kanji} {animalInfo.en.toUpperCase()} ·{' '}
          {elInfo.kanji} {elInfo.en.toUpperCase()}
        </span>
        <span className="unit-tooltip__eyebrow-tier">
          TIER {TIER_ROMAN[unit.tier]}
        </span>
      </div>
      <div className="unit-tooltip__name">{unit.name}</div>
      <div className="unit-tooltip__trigger">
        △ {formatTrigger(unit.ability.trigger)}
      </div>
      <ul className="unit-tooltip__stars">
        {(['1', '2', '3'] as const).map((k) => {
          const spec = starSpecs[k]
          const starN = Number(k) as Star
          const isCurrent = starN === currentStar
          const classes = [
            'unit-tooltip__star',
            k === '3' && 'unit-tooltip__star--max',
            isCurrent && 'unit-tooltip__star--current',
          ]
            .filter(Boolean)
            .join(' ')
          return (
            <li key={k} className={classes}>
              <span className="unit-tooltip__marker">
                {'✦'.repeat(starN)}
              </span>
              <span>{spec.description}</span>
            </li>
          )
        })}
      </ul>
    </div>
  )
}

interface SynergyRowProps {
  kind: 'element' | 'animal'
  id: Element | Animal
  count: number
}

function SynergyRow({ kind, id, count }: SynergyRowProps): ReactElement {
  const info =
    kind === 'element'
      ? ELEMENT_INFO[id as Element]
      : ANIMAL_INFO[id as Animal]
  const name = info.en.charAt(0).toUpperCase() + info.en.slice(1)
  const kanji = 'kanji' in info ? info.kanji : ''
  const tierIdx = count >= 5 ? 3 : count >= 3 ? 2 : count >= 2 ? 1 : 0
  const desc = describeSynergy(kind, id, count)
  const active = tierIdx > 0
  // Row accent color drives the left-edge bar + kanji color. Elements
  // use their own --el-{x}; animals map to a canonical element so
  // the palette stays tight (matches shop-new.jsx SYN_COLORS).
  const accent = synergyAccent(kind, id)
  return (
    <div
      className={[
        'synergy-row',
        active ? 'synergy-row--active' : 'synergy-row--inactive',
      ]
        .filter(Boolean)
        .join(' ')}
      style={{ ['--syn-accent' as string]: accent }}
      title={desc}
    >
      <div className="synergy-row__top">
        <span className="synergy-row__title">
          <span className="synergy-row__kanji" aria-hidden>
            {kanji}
          </span>
          <span className="synergy-row__name">{name}</span>
        </span>
        <span className="synergy-row__count">×{count}</span>
      </div>
      <span className="synergy-row__bars" aria-hidden>
        {[2, 3, 5].map((t) => (
          <span
            key={t}
            className={`synergy-row__bar synergy-row__bar--t${t} ${count >= t ? 'synergy-row__bar--filled' : ''}`}
          />
        ))}
      </span>
      <p className="synergy-row__desc">{desc}</p>
    </div>
  )
}

/** Maps a synergy row's (kind,id) to its accent color. Elements use
 *  their own --el-{x}; animals fall back to the element that
 *  thematically anchors them so the panel reads as a single 5-color
 *  palette rather than 10 different swatches. */
function synergyAccent(kind: 'element' | 'animal', id: string): string {
  if (kind === 'element') return `var(--el-${id})`
  switch (id) {
    case 'bird':
      return 'var(--el-wind)'
    case 'reptile':
      return 'var(--el-wood)'
    case 'insect':
      return 'var(--el-earth)'
    case 'fish':
      return 'var(--el-water)'
    case 'mammal':
      return 'var(--el-fire)'
    default:
      return 'var(--ink)'
  }
}

function describeSynergy(
  kind: 'element' | 'animal',
  id: string,
  count: number,
): string {
  return describeSynergyDetailed(kind, id, count)
}

/** Map combine.ts's rejection reasons to short user-facing hint text
 * shown on the invalid drop target. Spec §6.2 calls this out as a
 * required UX affordance for the manual combine flow. */
function invalidReasonText(reason: string): string {
  switch (reason) {
    case 'different units':
      return 'Same unit only'
    case 'different stars':
      return 'Same star only'
    case 'target capped at ★3':
      return 'Already at ★3 max'
    case 'same slot':
      return 'Drop on a different slot'
    default:
      return 'Same-unit, same-star only'
  }
}

interface BoardSlotProps {
  unit: BoardUnit | null
  index: number
  where: 'active' | 'bench'
  /** Click-to-select state — when true, the slot draws its
   *  highlight ring and the contextual SellTicket above the board
   *  reads its refund value. */
  selected?: boolean
  /** Toggle click-selection. Drag still bypasses this — pointer
   *  sensor's 4px activation distance keeps simple clicks firing. */
  onSelect?: () => void
  /** Mobile-landscape rendering. Stat images and glyph shrink;
   *  `.board-slot--compact` lets CSS trim padding. */
  compact?: boolean
  /** Floating HP/ATK delta animations queued by a recent shop action
   *  (on_purchase / on_combine_witnessed / on_shop_round_pass). The
   *  slot renders one absolute-positioned floater per entry. */
  floaters?: BuffFloater[]
  /** Called when a floater's CSS animation finishes so the store can
   *  evict it from its queue. */
  onFloaterDone?: (id: string) => void
  /** In-place fill-tick combine effect (non-star-up merges) — a
   *  short starburst + progress chip at this slot. Keyed by id so
   *  repeat merges on the same slot restart the animation. */
  fillFx?: { id: number; tickedDot: 1 | 2 | 3 } | null
}

function BoardSlot({
  unit,
  index,
  where,
  selected = false,
  onSelect,
  compact = false,
  floaters = [],
  onFloaterDone,
  fillFx = null,
}: BoardSlotProps): ReactElement {
  // 0-indexed for the drag id (matches array indexing); the visible
  // "1" badge stays 1-indexed via the `index` prop.
  const id = `board:${where}:${index - 1}`
  const draggable = useDraggable({
    id,
    disabled: !unit,
    data: unit ? { unit, where, index: index - 1 } : undefined,
  })
  const droppable = useDroppable({ id })
  const { active } = useDndContext()

  // Compute drop-target validity for visual feedback. Source can be
  // either a board unit (move/combine) or a shop slot's unit
  // (synthesized as a fresh ★1 fill-1 instance for combine checks).
  let validity: 'none' | 'valid-combine' | 'valid-move' | 'valid-swap' | 'invalid' = 'none'
  let invalidHint: string | null = null
  let refundHint: string | null = null
  // Source-kind hint surfaces "SWAP" alongside the rejection tooltip
  // when both source and target are board units (drag-board-onto-board
  // with mismatched units falls through to a reorder/swap rather than
  // a hard rejection — see handleDragEnd).
  let sourceIsBoardUnit = false
  if (active && active.id !== id) {
    const data = active.data.current as
      | { unit?: BoardUnit; kind?: string; slotUnit?: UnitDef }
      | undefined
    let source: BoardUnit | null = null
    if (data?.unit) {
      source = data.unit
      sourceIsBoardUnit = true
    } else if (data?.kind === 'shop' && data.slotUnit) {
      source = {
        unit: data.slotUnit,
        star: 1,
        fill: 1,
        hp: data.slotUnit.stats.hp,
        atk: data.slotUnit.stats.atk,
      }
    }
    if (source) {
      if (!unit) {
        validity = 'valid-move'
      } else {
        const check = canCombine(source, unit)
        if (check === true) {
          validity = 'valid-combine'
          // Preview the would-be refund: the lower-fill unit is the
          // one that gets consumed; its fill above 1 is the refund.
          const consumedFill = Math.min(source.fill, unit.fill)
          const refund = Math.max(0, consumedFill - 1)
          if (refund > 0) refundHint = `+${refund}◈`
        } else if (sourceIsBoardUnit) {
          // Board → board with mismatched units swaps instead of
          // erroring. Surface both: the rejection reason (why it
          // didn't combine) plus a "swap" affordance under the card.
          validity = 'valid-swap'
          invalidHint = invalidReasonText(check)
        } else {
          validity = 'invalid'
          invalidHint = invalidReasonText(check)
        }
      }
    }
  }

  const setRef = (node: HTMLElement | null): void => {
    draggable.setNodeRef(node)
    droppable.setNodeRef(node)
  }

  // Intentionally NOT applying draggable.transform — the DragOverlay
  // portal carries the visual movement on a single composited
  // layer. See the DragPreview comment above for rationale.

  const overClass = droppable.isOver ? `board-slot--over board-slot--over-${validity}` : ''
  const dragClass = draggable.isDragging ? 'board-slot--dragging' : ''

  if (!unit) {
    return (
      <div
        ref={setRef}
        className={[
          'board-slot',
          'board-slot--empty',
          compact && 'board-slot--compact',
          overClass,
        ]
          .filter(Boolean)
          .join(' ')}
      >
        <span className="board-slot__empty-mark">
          {where === 'bench' ? '+' : index}
        </span>
      </div>
    )
  }
  const capped = unit.star === 3 && unit.fill === 3
  // Sell value = tier × star. Stays in sync with handleSell.
  const sellPrice = unit.unit.tier * unit.star
  // Forged-frame board card. Mirrors the shop card layout but swaps
  // two zones: name banner → star indicator (✦ × current star), and
  // animal chip → fill-pill (3-pip combine counter; capped at ★3 +
  // fill=3 tints filled pips gold). Frame asset is keyed by current
  // star so chrome itself escalates ★1 → ★2 → ★3 with each combine.
  // See ForgedBoardCard in design/Gallery.tsx for the visual source.
  return (
    <div
      ref={setRef}
      {...draggable.attributes}
      {...draggable.listeners}
      className={[
        'board-slot board-slot--forged',
        `board-slot--el-${unit.unit.element}`,
        selected && 'board-slot--selected',
        compact && 'board-slot--compact',
        overClass,
        dragClass,
      ]
        .filter(Boolean)
        .join(' ')}
      style={{
        ['--frame-url' as never]: `url(${forgedFrameUrl(unit.unit.element, unit.star)})`,
        zIndex: selected ? 5 : undefined,
      }}
      onClick={(e) => {
        e.stopPropagation()
        playSfx('ui-tap')
        onSelect?.()
      }}
      role="button"
      tabIndex={0}
      aria-pressed={selected}
    >
      {floaters.length > 0 && (
        <div className="board-slot__floaters" aria-hidden="true">
          {floaters.map((f) => {
            // Combine-triggered floaters carry a notBeforeMs so they
            // don't render under the EvolutionSequence scrim. The
            // keyframe starts at opacity:0 so the element is visually
            // absent during the CSS animation-delay window.
            const delay = f.notBeforeMs
              ? Math.max(0, f.notBeforeMs - performance.now())
              : 0
            let text: string
            if (f.stat === 'consumed') {
              text = `${f.label ?? 'unit'} eaten`
            } else if (f.stat === 'tribute') {
              text = `−${f.delta}g tribute`
            } else {
              text = `+${f.delta} ${f.stat.toUpperCase()}`
            }
            return (
              <span
                key={f.id}
                className={`board-slot__floater board-slot__floater--${f.stat}`}
                style={delay > 0 ? { animationDelay: `${delay}ms` } : undefined}
                onAnimationEnd={() => onFloaterDone?.(f.id)}
              >
                {text}
              </span>
            )
          })}
        </div>
      )}
      {fillFx && (
        <div className="board-slot__fillfx" key={fillFx.id} aria-hidden="true">
          <svg className="board-slot__fillfx-burst" viewBox="0 0 120 120">
            <path
              d="M 60,4 L 71,38 L 104,29 L 81,53 L 116,60 L 81,67 L 104,91 L 71,82 L 60,116 L 49,82 L 16,91 L 39,67 L 4,60 L 39,53 L 16,29 L 49,38 Z"
              fill="var(--gold)"
              stroke="var(--ink)"
              strokeWidth="5"
              strokeLinejoin="round"
            />
            <circle cx="60" cy="60" r="16" fill="#fff" opacity="0.9" />
          </svg>
          <span className="board-slot__fillfx-tick">{fillFx.tickedDot}/3</span>
        </div>
      )}
      {(validity === 'invalid' || validity === 'valid-swap') &&
        droppable.isOver &&
        invalidHint && (
          <span className="board-slot__invalid-tip" role="tooltip">
            {invalidHint}
          </span>
        )}
      {validity === 'valid-swap' && droppable.isOver && (
        <span className="board-slot__swap-tip" role="tooltip">
          ↺ swap
        </span>
      )}
      {validity === 'valid-combine' && droppable.isOver && refundHint && (
        <span className="board-slot__refund-tip" role="tooltip">
          REFUND {refundHint}
        </span>
      )}

      <div
        className="board-slot__forge-stars"
        aria-label={`star ${unit.star}`}
      >
        {'✦'.repeat(unit.star)}
      </div>
      <div
        className="board-slot__forge-tier"
        aria-label={`tier ${TIER_ROMAN[unit.unit.tier]}`}
      >
        {TIER_ROMAN[unit.unit.tier]}
      </div>

      <div className="board-slot__forge-art">
        {where === 'bench' ? (
          <div>
            <TieredCritter
              glyph={unit.unit.glyph}
              star={unit.star}
              size={compact ? 44 : 88}
              unitId={unit.unit.id}
            />
          </div>
        ) : (
          <div className="bob" style={{ animationDelay: `${index * 0.3}s` }}>
            <TieredCritter
              glyph={unit.unit.glyph}
              star={unit.star}
              size={compact ? 44 : 88}
              unitId={unit.unit.id}
            />
          </div>
        )}
      </div>

      <span
        className={[
          'board-slot__forge-hp',
          (unit.accruedBench?.hp ?? 0) > 0 && 'board-slot__forge-hp--accrued',
        ]
          .filter(Boolean)
          .join(' ')}
        aria-label={`HP ${unit.hp}`}
      >
        {unit.hp}
      </span>
      <span
        className={[
          'board-slot__forge-atk',
          (unit.accruedBench?.atk ?? 0) > 0 && 'board-slot__forge-atk--accrued',
        ]
          .filter(Boolean)
          .join(' ')}
        aria-label={`ATK ${unit.atk}`}
      >
        {unit.atk}
      </span>

      <div
        className="board-slot__forge-fill"
        aria-label={capped ? 'capped' : `${unit.fill} of 3`}
      >
        {[1, 2, 3].map((i) => {
          const filled = i <= unit.fill
          return (
            <span
              key={i}
              className={[
                'board-slot__forge-pip',
                filled && 'board-slot__forge-pip--filled',
                filled && capped && 'board-slot__forge-pip--capped',
              ]
                .filter(Boolean)
                .join(' ')}
            />
          )
        })}
      </div>

      <UnitTooltip
        unit={unit.unit}
        placement="above"
        currentStar={unit.star}
        sellPrice={sellPrice}
      />
    </div>
  )
}

/** 質札 (shichi-fuda) — pawn ticket. A small paper slip that
 *  appears above the board when a lineup/bench unit is selected,
 *  carrying the refund value (tier × star) and acting as the
 *  click-to-sell button.
 *
 *  Always mounted; visibility flips via the .sell-ticket--visible
 *  modifier so CSS transitions can animate both enter and exit.
 *  The button is disabled (pointer-events: none, aria-hidden) when
 *  no unit is selected, so it's both visually and a11y-hidden in
 *  the idle state. */
function SellTicket({
  unit,
  onSell,
}: {
  unit: BoardUnit | null
  onSell: () => void
}): ReactElement {
  // Refund stays at the last-known value during the exit animation
  // (so the digit doesn't briefly read "+0" as the ticket fades out).
  const [lastRefund, setLastRefund] = useState(0)
  useEffect(() => {
    if (unit) setLastRefund(unit.unit.tier * unit.star)
  }, [unit])
  const visible = unit !== null
  const refund = visible ? unit!.unit.tier * unit!.star : lastRefund

  return (
    <button
      type="button"
      className={`sell-ticket ${visible ? 'sell-ticket--visible' : ''}`}
      onClick={(e) => {
        e.stopPropagation()
        if (visible) onSell()
      }}
      disabled={!visible}
      aria-hidden={!visible}
      aria-label={
        visible ? `Sell selected unit for ${refund} gold` : 'No unit selected'
      }
      title={visible ? `Sell for ${refund} gold` : undefined}
    >
      {/* cord hanging the ticket from above — two strands from a
         single knot down to the eyelet dots on the ticket. */}
      <span className="sell-ticket__cord" aria-hidden>
        <svg viewBox="0 0 160 20" preserveAspectRatio="none">
          <path
            d="M 80 3 L 22 20"
            stroke="var(--vermillion-deep)"
            strokeWidth="1.6"
            strokeLinecap="round"
            fill="none"
          />
          <path
            d="M 80 3 L 138 20"
            stroke="var(--vermillion-deep)"
            strokeWidth="1.6"
            strokeLinecap="round"
            fill="none"
          />
          <circle
            cx="80"
            cy="3"
            r="3"
            fill="var(--vermillion-deep)"
            stroke="var(--ink)"
            strokeWidth="1"
          />
        </svg>
      </span>
      {/* eyelet ink dots — where the cord ties in */}
      <span className="sell-ticket__eyelet sell-ticket__eyelet--l" aria-hidden />
      <span className="sell-ticket__eyelet sell-ticket__eyelet--r" aria-hidden />
      {/* 賣 — old variant of 売, fits the "vintage paper ticket" feel */}
      <span className="sell-ticket__kanji" aria-hidden>
        賣
      </span>
      <span className="sell-ticket__perforation" aria-hidden />
      <span className="sell-ticket__body">
        <span className="sell-ticket__label">SELL</span>
        <span className="sell-ticket__value">
          +{refund}
          <span className="sell-ticket__coin" aria-hidden>
            ◈
          </span>
        </span>
      </span>
    </button>
  )
}

// === Mobile shop layout ====================================================
//
// Phone-landscape variant. Forked at the JSX level via useIsMobile (see
// CLAUDE.md "Mobile-only layout fork via JSX, not CSS"). Reuses the
// existing ShopCard / BoardSlot / SellTicket / SynergyRow components with
// compact sizing and a parallel `.sh-m-*` CSS namespace.

interface MobileShopProps {
  state: ShopState
  actions: ShopActions
  isReady: boolean
  synergyCounts: SynergyCounts
  selectedSlotIndex: number | null
  setSelectedSlotIndex: (i: number | null) => void
  selectedBoardLoc: CombineLocation | null
  setSelectedBoardLoc: (
    updater:
      | CombineLocation
      | null
      | ((prev: CombineLocation | null) => CombineLocation | null),
  ) => void
  selectedSlot: ShopSlot | null | undefined
  selectedBoardUnit: BoardUnit | null
  /** Tap-to-place handler — when a shop card or board unit is already
   *  selected, tapping a board slot resolves the action (buy / combine /
   *  move / swap) and fires the evolution overlay if applicable. Returns
   *  true when a placement happened so the caller skips the default
   *  toggle-selection behavior. */
  tryTapPlace: (target: CombineLocation) => boolean
  /** Shop-phase HP/ATK delta animations queued by the store after
   *  on_purchase / on_combine_witnessed / on_shop_round_pass / etc.
   *  Same shape the desktop tree consumes — each variant filters by
   *  where + position before forwarding to its BoardSlot. */
  buffFloaters?: BuffFloater[]
  onClearFloater?: (id: string) => void
  /** Resolve the in-place fill-tick fx for a board location — owned
   *  by the outer Shop (which fires it from fireEvolution). */
  fillFxFor: (kind: 'active' | 'bench', index: number) => { id: number; tickedDot: 1 | 2 | 3 } | null
}

function MobileShop({
  state,
  actions,
  isReady,
  synergyCounts,
  selectedSlotIndex,
  setSelectedSlotIndex,
  selectedBoardLoc,
  setSelectedBoardLoc,
  selectedSlot,
  selectedBoardUnit,
  tryTapPlace,
  buffFloaters = [],
  onClearFloater,
  fillFxFor,
}: MobileShopProps): ReactElement {
  // Mounted only in landscape — `.sh-m--landscape` hides itself in
  // portrait via media query, and the shared <RotatePrompt /> shows on
  // top. See packages/client/src/ui/useIsMobile.css for the gate.

  // Mirror the desktop "shop card with a board match bobs" cue.
  const allBoardUnits = [...state.activeLine, ...state.bench]
  const hasCombineMatch = (slot: ShopSlot): boolean => {
    if (slot.bought) return false
    const synthetic: BoardUnit = {
      unit: slot.unit,
      star: 1,
      fill: 1,
      hp: slot.unit.stats.hp,
      atk: slot.unit.stats.atk,
    }
    return allBoardUnits.some(
      (u) => u !== null && canCombine(synthetic, u) === true,
    )
  }

  const indexed = state.shopSlots.map((slot, i) => ({ slot, i }))
  const baseEntries = indexed.filter((x) => !x.slot.isBonus)
  const bonusEntries = indexed.filter((x) => x.slot.isBonus)

  return (
    <div className="paper paper--atelier sh-m-page sh-m--landscape">
      <MobileShopHeader state={state} actions={actions} isReady={isReady} />
      <div className="sh-m-body">
        <section className="sh-m-drafting">
          <div className="sh-m-drafting__row">
            {baseEntries.map(({ slot, i }) => (
              <ShopCard
                key={i}
                slot={slot}
                slotIndex={i}
                selected={selectedSlotIndex === i}
                hasCombineMatch={hasCombineMatch(slot)}
                compact
                onSelect={() => {
                  setSelectedBoardLoc(null)
                  setSelectedSlotIndex(i === selectedSlotIndex ? null : i)
                }}
                onDoubleBuy={() => {
                  actions.onBuy?.(i)
                  setSelectedSlotIndex(null)
                }}
                onContextLock={() => actions.onToggleSlotLock?.(i)}
              />
            ))}
            {bonusEntries.map(({ slot, i }) => (
              <ShopCard
                key={i}
                slot={slot}
                slotIndex={i}
                selected={selectedSlotIndex === i}
                hasCombineMatch={hasCombineMatch(slot)}
                compact
                onSelect={() => {
                  setSelectedBoardLoc(null)
                  setSelectedSlotIndex(i === selectedSlotIndex ? null : i)
                }}
                onDoubleBuy={() => {
                  actions.onBuy?.(i)
                  setSelectedSlotIndex(null)
                }}
                onContextLock={() => actions.onToggleSlotLock?.(i)}
              />
            ))}
          </div>
        </section>

        <section className="sh-m-board">
          <div className="sh-m-board__rails">
            <div className="sh-m-board__rail sh-m-board__rail--lineup">
              <div className="sh-m-board__rail-head">
                <span className="sh-m-board__rail-label tiny-cap">
                  LINEUP ✦
                </span>
              </div>
              <div className="sh-m-board__slots">
                {state.activeLine
                  .map((unit, i) => ({ unit, i }))
                  .reverse()
                  .map(({ unit, i }) => (
                    <BoardSlot
                      key={i}
                      unit={unit}
                      index={i + 1}
                      where="active"
                      compact
                      selected={
                        selectedBoardLoc?.kind === 'active' &&
                        selectedBoardLoc.index === i
                      }
                      floaters={buffFloaters.filter(
                        (f) => f.where === 'active' && f.position === i,
                      )}
                      onFloaterDone={onClearFloater}
                      fillFx={fillFxFor('active', i)}
                      onSelect={() => {
                        // Tap-to-place first; on miss, toggle selection.
                        if (tryTapPlace({ kind: 'active', index: i })) return
                        setSelectedSlotIndex(null)
                        setSelectedBoardLoc((prev) =>
                          prev?.kind === 'active' && prev.index === i
                            ? null
                            : { kind: 'active', index: i },
                        )
                      }}
                    />
                  ))}
              </div>
            </div>
            <div className="sh-m-board__rail sh-m-board__rail--bench">
              <div className="sh-m-board__rail-head">
                <span className="sh-m-board__rail-label tiny-cap">
                  BENCH 💤
                </span>
              </div>
              <div className="sh-m-board__slots">
                {state.bench.map((unit, i) => (
                  <BoardSlot
                    key={i}
                    unit={unit}
                    index={i + 1}
                    where="bench"
                    compact
                    selected={
                      selectedBoardLoc?.kind === 'bench' &&
                      selectedBoardLoc.index === i
                    }
                    floaters={buffFloaters.filter(
                      (f) => f.where === 'bench' && f.position === i,
                    )}
                    onFloaterDone={onClearFloater}
                    fillFx={fillFxFor('bench', i)}
                    onSelect={() => {
                      if (tryTapPlace({ kind: 'bench', index: i })) return
                      setSelectedSlotIndex(null)
                      setSelectedBoardLoc((prev) =>
                        prev?.kind === 'bench' && prev.index === i
                          ? null
                          : { kind: 'bench', index: i },
                      )
                    }}
                  />
                ))}
              </div>
            </div>
          </div>
        </section>
      </div>
      <SynergyTray counts={synergyCounts} />
      <MobileTooltipTray
        selectedSlot={selectedSlot}
        selectedBoardUnit={selectedBoardUnit}
      />
      <MobileActionBar
        selectedSlot={selectedSlot}
        selectedSlotIndex={selectedSlotIndex}
        selectedBoardLoc={selectedBoardLoc}
        selectedBoardUnit={selectedBoardUnit}
        gold={state.gold}
        onBuy={actions.onBuy}
        onToggleSlotLock={actions.onToggleSlotLock}
        onSell={actions.onSell}
        clearShopSelection={() => setSelectedSlotIndex(null)}
        clearBoardSelection={() => setSelectedBoardLoc(null)}
      />
    </div>
  )
}

/** Right-edge action bar for mobile. Slides in when a shop card or
 *  board unit is selected:
 *    • shop selection → Buy (with cost) + Lock toggle
 *    • board selection → Sell (with refund value = tier × star)
 *  Frees the vertical row the prior in-flow section-bar consumed.
 *  Auto-hides when nothing is selected; consumer clears selection on
 *  every action path so the bar slides back out cleanly. */
function MobileActionBar({
  selectedSlot,
  selectedSlotIndex,
  selectedBoardLoc,
  selectedBoardUnit,
  gold,
  onBuy,
  onToggleSlotLock,
  onSell,
  clearShopSelection,
  clearBoardSelection,
}: {
  selectedSlot: ShopSlot | null | undefined
  selectedSlotIndex: number | null
  selectedBoardLoc: CombineLocation | null
  selectedBoardUnit: BoardUnit | null
  gold: number
  onBuy?: (slotIndex: number) => void
  onToggleSlotLock?: (slotIndex: number) => void
  onSell?: (where: 'active' | 'bench', index: number) => void
  clearShopSelection: () => void
  clearBoardSelection: () => void
}): ReactElement {
  const isShop = selectedSlot != null && selectedSlotIndex != null
  const isBoard = selectedBoardLoc != null && selectedBoardUnit != null
  const open = isShop || isBoard
  const sellPrice =
    selectedBoardUnit != null
      ? selectedBoardUnit.unit.tier * selectedBoardUnit.star
      : 0
  // Vertical anchor mirrors MobileTooltipTray: shop selection (top
  // half of the screen) → bar lands in the BOTTOM half so it sits
  // away from the cards the user is reading; board selection (bottom
  // half) → bar lands in the TOP half.
  const anchor: 'top' | 'bottom' = isBoard ? 'top' : 'bottom'
  return (
    <div
      className={[
        'sh-m-action-bar',
        `sh-m-action-bar--${anchor}`,
        open && 'sh-m-action-bar--open',
      ]
        .filter(Boolean)
        .join(' ')}
      aria-hidden={!open}
    >
      {isShop && selectedSlot && selectedSlotIndex !== null && (
        <>
          <button
            type="button"
            className="sh-m-btn sh-m-btn--buy"
            onClick={() => {
              onBuy?.(selectedSlotIndex)
              clearShopSelection()
            }}
            disabled={!selectedSlot.isBonus && gold < selectedSlot.cost}
          >
            <span className="sh-m-btn__label">Buy</span>
            <span className="sh-m-btn__value">
              {selectedSlot.isBonus ? 'free' : `${selectedSlot.cost}◈`}
            </span>
          </button>
          {/* Bonus slot lock is a no-op (replaced next reroll), so
              hide the toggle to keep the action bar clean. */}
          {!selectedSlot.isBonus && (
            <button
              type="button"
              className={`sh-m-btn ${selectedSlot.locked ? 'sh-m-btn--locked' : ''}`}
              onClick={() => onToggleSlotLock?.(selectedSlotIndex)}
              aria-label={selectedSlot.locked ? 'Unlock slot' : 'Lock slot'}
            >
              <span className="sh-m-btn__label">
                {selectedSlot.locked ? 'Unlock' : 'Lock'}
              </span>
              <span className="sh-m-btn__value">
                {selectedSlot.locked ? '🔒' : '🔓'}
              </span>
            </button>
          )}
        </>
      )}
      {isBoard && selectedBoardLoc && (
        <button
          type="button"
          className="sh-m-btn sh-m-btn--sell"
          onClick={() => {
            onSell?.(selectedBoardLoc.kind, selectedBoardLoc.index)
            clearBoardSelection()
          }}
        >
          <span className="sh-m-btn__label">Sell</span>
          <span className="sh-m-btn__value">{sellPrice}◈</span>
        </button>
      )}
    </div>
  )
}

/** Left-edge tooltip tray for mobile. Slides in from the left whenever
 *  a shop card or board unit is selected. Anchored to the BOTTOM half
 *  when a shop card is selected (so the tray lands away from the
 *  drafting row the user is reading); anchored to the TOP half when a
 *  board unit is selected (so the tray lands away from the lineup /
 *  bench the user is reading). Auto-closes on deselect or when any
 *  action (combine / sell / buy / move) clears the selection, since
 *  the consumer of MobileShop clears `selected*` on those paths. */
function MobileTooltipTray({
  selectedSlot,
  selectedBoardUnit,
}: {
  selectedSlot: ShopSlot | null | undefined
  selectedBoardUnit: BoardUnit | null
}): ReactElement {
  type Snapshot = {
    source: 'shop' | 'board'
    unit: UnitDef
    currentStar: Star
    sellPrice?: number
  }
  const liveSnapshot: Snapshot | null = selectedSlot
    ? {
        source: 'shop',
        unit: selectedSlot.unit,
        currentStar: 1,
      }
    : selectedBoardUnit
      ? {
          source: 'board',
          unit: selectedBoardUnit.unit,
          currentStar: selectedBoardUnit.star,
          sellPrice: selectedBoardUnit.unit.tier * selectedBoardUnit.star,
        }
      : null

  // Hold the last non-null snapshot so the content stays rendered
  // during the close animation. New selection updates it immediately;
  // clearing the selection leaves the previous content visible until
  // the slide-out finishes.
  const [shown, setShown] = useState<Snapshot | null>(liveSnapshot)
  useEffect(() => {
    if (liveSnapshot) setShown(liveSnapshot)
    // Depend on the stable selection props, NOT liveSnapshot — it's a
    // fresh object every render, and using it as a dep loops this
    // effect (setShown → render → new snapshot → effect …) for as
    // long as a selection is open ("Maximum update depth exceeded").
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSlot, selectedBoardUnit])

  // Auto-collapse the tray ~2s after a new selection so the tooltip
  // doesn't sit indefinitely over a bench slot the player needs for
  // the next action (combine target, sell target). Selection itself
  // stays alive — only the visual tray retracts. Tapping a different
  // card resets the timer; deselect (or any action) closes it
  // immediately by clearing liveSnapshot.
  const [visible, setVisible] = useState(false)
  const selectionKey = liveSnapshot
    ? `${liveSnapshot.source}:${liveSnapshot.unit.id}:${liveSnapshot.currentStar}`
    : null
  useEffect(() => {
    if (selectionKey === null) {
      setVisible(false)
      return
    }
    setVisible(true)
    const t = window.setTimeout(() => setVisible(false), 2000)
    return () => window.clearTimeout(t)
  }, [selectionKey])

  const open = visible
  const anchor = (liveSnapshot ?? shown)?.source === 'shop' ? 'bottom' : 'top'
  const content = shown

  return (
    <div
      className={[
        'sh-m-tt-tray',
        `sh-m-tt-tray--${anchor}`,
        open && 'sh-m-tt-tray--open',
      ]
        .filter(Boolean)
        .join(' ')}
      aria-hidden={!open}
    >
      {content && (
        <div className="sh-m-tt-tray__content">
          <div className="sh-m-tt-tray__watermark" aria-hidden>
            <span
              className="sh-m-tt-tray__wm sh-m-tt-tray__wm--element"
              style={{
                color: ELEMENT_INFO[content.unit.element].bg,
              }}
            >
              {ELEMENT_INFO[content.unit.element].kanji}
            </span>
            <span
              className="sh-m-tt-tray__wm sh-m-tt-tray__wm--animal"
              style={{ color: ANIMAL_INFO[content.unit.animal].bg }}
            >
              {ANIMAL_INFO[content.unit.animal].kanji}
            </span>
          </div>
          <div className="sh-m-tt-tray__eyebrow">
            <span>
              {ANIMAL_INFO[content.unit.animal].kanji}{' '}
              {ANIMAL_INFO[content.unit.animal].en.toUpperCase()} ·{' '}
              {ELEMENT_INFO[content.unit.element].kanji}{' '}
              {ELEMENT_INFO[content.unit.element].en.toUpperCase()}
            </span>
            <span className="sh-m-tt-tray__eyebrow-tier">
              TIER {TIER_ROMAN[content.unit.tier]}
            </span>
          </div>
          <div className="sh-m-tt-tray__name">{content.unit.name}</div>
          <div className="sh-m-tt-tray__trigger-row">
            <span className="sh-m-tt-tray__trigger">
              △ {formatTrigger(content.unit.ability.trigger)}
            </span>
            {content.sellPrice !== undefined && (
              <span className="sh-m-tt-tray__sell">
                SELL {content.sellPrice}◈
              </span>
            )}
          </div>
          <ul className="sh-m-tt-tray__stars">
            {(['1', '2', '3'] as const).map((k) => {
              const spec = content.unit.ability.stars[k]
              const starN = Number(k) as Star
              const isCurrent = starN === content.currentStar
              return (
                <li
                  key={k}
                  className={[
                    'sh-m-tt-tray__star',
                    k === '3' && 'sh-m-tt-tray__star--max',
                    isCurrent && 'sh-m-tt-tray__star--current',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                >
                  <span className="sh-m-tt-tray__marker">
                    {'✦'.repeat(starN)}
                  </span>
                  <span>{spec.description}</span>
                </li>
              )
            })}
          </ul>
        </div>
      )}
    </div>
  )
}

function SynergyTray({
  counts,
}: {
  counts: SynergyCounts
}): ReactElement {
  const [open, setOpen] = useState(false)
  const elKeys = (['fire', 'water', 'wind', 'wood', 'earth'] as const).filter(
    (e) => (counts.element[e] ?? 0) >= 2,
  )
  const anKeys = (
    ['bird', 'mammal', 'reptile', 'fish', 'insect'] as const
  ).filter((a) => (counts.animal[a] ?? 0) >= 2)
  const total = elKeys.length + anKeys.length

  // Collapsed handle: one-line ticker of achieved synergies.
  const ticker =
    total === 0
      ? 'no synergies yet · pair 2+ of an element or animal'
      : [
          ...elKeys.map(
            (e) =>
              `${ELEMENT_INFO[e].kanji} ${ELEMENT_INFO[e].en}×${counts.element[e]}`,
          ),
          ...anKeys.map(
            (a) =>
              `${ANIMAL_INFO[a].kanji} ${ANIMAL_INFO[a].en}×${counts.animal[a]}`,
          ),
        ].join(' · ')

  return (
    <div
      className={`sh-m-synergies-tray ${open ? 'sh-m-synergies-tray--open' : ''}`}
    >
      <button
        type="button"
        className="sh-m-synergies-tray__handle"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-label={open ? 'Hide synergies' : 'Show synergies'}
      >
        <span aria-hidden className="sh-m-synergies-tray__chevron">
          {open ? '›' : '‹'}
        </span>
        <span className="sh-m-synergies-tray__label tiny-cap">SYNERGIES</span>
      </button>
      {/* Collapsed strip: vertical stack of achieved-synergy kanji
          chips so the player gets at-a-glance visibility without
          opening the drawer. Tap any chip area (the whole strip is
          the toggle button) to open. */}
      {!open && (
        <div className="sh-m-synergies-tray__rail" aria-hidden>
          {elKeys.map((el) => (
            <span
              key={`el-${el}`}
              className="sh-m-synergies-tray__chip"
              style={{ ['--syn-accent' as string]: `var(--el-${el})` }}
              title={`${ELEMENT_INFO[el].en} ×${counts.element[el]}`}
            >
              {ELEMENT_INFO[el].kanji}
            </span>
          ))}
          {anKeys.map((an) => (
            <span
              key={`an-${an}`}
              className="sh-m-synergies-tray__chip"
              style={{ ['--syn-accent' as string]: synergyAccent('animal', an) }}
              title={`${ANIMAL_INFO[an].en} ×${counts.animal[an]}`}
            >
              {ANIMAL_INFO[an].kanji}
            </span>
          ))}
          {total === 0 && (
            <span className="sh-m-synergies-tray__chip sh-m-synergies-tray__chip--empty">
              —
            </span>
          )}
        </div>
      )}
      {open && (
        <div className="sh-m-synergies-tray__body">
          <div className="sh-m-synergies-tray__group">
            <div className="sh-m-synergies-tray__group-label tiny-cap">
              ELEMENT
            </div>
            {elKeys.length === 0 ? (
              <p className="hand-note sh-m-synergies-tray__empty">none yet</p>
            ) : (
              elKeys.map((el) => (
                <SynergyRow
                  key={el}
                  kind="element"
                  id={el}
                  count={counts.element[el] ?? 0}
                />
              ))
            )}
          </div>
          <div className="sh-m-synergies-tray__group">
            <div className="sh-m-synergies-tray__group-label tiny-cap">
              ANIMAL
            </div>
            {anKeys.length === 0 ? (
              <p className="hand-note sh-m-synergies-tray__empty">none yet</p>
            ) : (
              anKeys.map((an) => (
                <SynergyRow
                  key={an}
                  kind="animal"
                  id={an}
                  count={counts.animal[an] ?? 0}
                />
              ))
            )}
          </div>
          <p className="hand-note sh-m-synergies-tray__note">
            counts active 5 only ✦ thresholds 2 / 3 / 5 ✦ applied at battle start
          </p>
        </div>
      )}
      {/* `ticker` is no longer rendered (the collapsed rail replaced
          it) but kept here in a hidden node so the variable stays
          referenced — useful aria-label fallback if we re-add a
          text representation later. */}
      <span style={{ display: 'none' }} aria-hidden>{ticker}</span>
    </div>
  )
}

function MobileShopHeader({
  state,
  actions,
  isReady,
}: {
  state: ShopState
  actions: ShopActions
  isReady: boolean
}): ReactElement {
  return (
    <header className="sh-m-header">
      {actions.onExit && (
        <button
          type="button"
          className="sh-m-header__exit"
          onClick={actions.onExit}
          title="Exit to title"
          aria-label="Exit to title"
        >
          <span aria-hidden>←</span>
        </button>
      )}
      {actions.onOpenTutorial && (
        <button
          type="button"
          className="sh-m-header__help"
          onClick={actions.onOpenTutorial}
          title="How to play"
          aria-label="How to play"
        >
          ?
        </button>
      )}
      <div className="sh-m-header__title">
        <span className="sh-m-header__kanji" aria-hidden>
          商
        </span>
        <span className="sh-m-header__wordmark">SHOP</span>
        <span className="sh-m-header__round">
          R{String(state.round).padStart(2, '0')}
        </span>
        <span className="sh-m-header__tier">
          TIER {TIER_ROMAN[state.shopLevel]}
        </span>
      </div>
      <div className="sh-m-header__stats">
        <span className="sh-m-stat sh-m-stat--gold" title="Gold">
          <span className="sh-m-stat__icon" aria-hidden>◈</span>
          <span className="sh-m-stat__value">
            {String(state.gold).padStart(2, '0')}
          </span>
        </span>
        <span className="sh-m-stat sh-m-stat--health" title="Health">
          <span className="sh-m-stat__icon" aria-hidden>♥</span>
          <span className="sh-m-stat__value">{state.health}</span>
          <span className="sh-m-stat__sub">/{state.maxHealth}</span>
        </span>
        <span className="sh-m-stat sh-m-stat--wins" title="Wins">
          <span className="sh-m-stat__icon" aria-hidden>🏆</span>
          <span className="sh-m-stat__value">{state.wins}</span>
          <span className="sh-m-stat__sub">/{state.maxWins}</span>
        </span>
      </div>
      <div className="sh-m-header__cta">
        <button
          type="button"
          className="sh-m-btn sh-m-btn--reroll"
          onClick={actions.onReroll}
          disabled={state.gold < state.rerollCost}
          title={`Reroll · ${state.rerollCost} gold`}
        >
          <span aria-hidden>↻</span>
          <span className="sh-m-btn__label">REROLL</span>
          <span className="sh-m-btn__cost">{state.rerollCost}◈</span>
        </button>
        <button
          type="button"
          className={`sh-m-btn sh-m-btn--ready${isReady ? ' sh-m-btn--ready-on' : ''}`}
          onClick={actions.onReady}
          aria-pressed={isReady}
        >
          {isReady ? '↺ UNREADY' : '✦ READY ✦'}
        </button>
      </div>
    </header>
  )
}

// === Fold shop layout ====================================================
//
// Galaxy Fold inner-display variant. Forked at the JSX level via
// useIsFoldOpen() — see CLAUDE.md "Fold-only layout fork via JSX".
// Two-panel DS-style split: the hinge runs horizontally across the
// physical device at y=50% (player rotates 90° to satisfy our
// landscape lock), so this layout treats the top half as the
// drafting "hand" and the bottom half as "your board". Nothing
// renders across the hinge. Reuses ShopCard / BoardSlot (with
// `compact`), MobileTooltipTray, MobileActionBar — same as
// MobileShop — under a parallel `.sh-fold-*` CSS namespace.

function FoldShop({
  state,
  actions,
  isReady,
  synergyCounts,
  selectedSlotIndex,
  setSelectedSlotIndex,
  selectedBoardLoc,
  setSelectedBoardLoc,
  selectedSlot,
  selectedBoardUnit,
  tryTapPlace,
  buffFloaters = [],
  onClearFloater,
  fillFxFor,
}: MobileShopProps): ReactElement {
  const allBoardUnits = [...state.activeLine, ...state.bench]
  const hasCombineMatch = (slot: ShopSlot): boolean => {
    if (slot.bought) return false
    const synthetic: BoardUnit = {
      unit: slot.unit,
      star: 1,
      fill: 1,
      hp: slot.unit.stats.hp,
      atk: slot.unit.stats.atk,
    }
    return allBoardUnits.some(
      (u) => u !== null && canCombine(synthetic, u) === true,
    )
  }

  const indexed = state.shopSlots.map((slot, i) => ({ slot, i }))
  const baseEntries = indexed.filter((x) => !x.slot.isBonus)
  const bonusEntries = indexed.filter((x) => x.slot.isBonus)

  return (
    <div className="paper paper--atelier sh-fold-page">
      {/* TOP HALF — drafting "hand" (header + 5 base cards + bonus cluster) */}
      <section className="sh-fold-top">
        <MobileShopHeader state={state} actions={actions} isReady={isReady} />
        <div className="sh-fold-drafting">
          <div className="sh-fold-drafting__label tiny-cap">
            <span aria-hidden>✦</span> DRAFTING TABLE
          </div>
          <div className="sh-fold-drafting__row">
            {baseEntries.map(({ slot, i }) => (
              <ShopCard
                key={i}
                slot={slot}
                slotIndex={i}
                selected={selectedSlotIndex === i}
                hasCombineMatch={hasCombineMatch(slot)}
                compact
                onSelect={() => {
                  setSelectedBoardLoc(null)
                  setSelectedSlotIndex(i === selectedSlotIndex ? null : i)
                }}
                onDoubleBuy={() => {
                  actions.onBuy?.(i)
                  setSelectedSlotIndex(null)
                }}
                onContextLock={() => actions.onToggleSlotLock?.(i)}
              />
            ))}
            {bonusEntries.length > 0 && (
              <div
                className={`sh-fold-drafting__bonus-cluster${bonusEntries.length > 1 ? ' sh-fold-drafting__bonus-cluster--multi' : ''}`}
              >
                {bonusEntries.map(({ slot, i }) => (
                  <ShopCard
                    key={i}
                    slot={slot}
                    slotIndex={i}
                    selected={selectedSlotIndex === i}
                    hasCombineMatch={hasCombineMatch(slot)}
                    compact
                    onSelect={() => {
                      setSelectedBoardLoc(null)
                      setSelectedSlotIndex(i === selectedSlotIndex ? null : i)
                    }}
                    onDoubleBuy={() => {
                      actions.onBuy?.(i)
                      setSelectedSlotIndex(null)
                    }}
                    onContextLock={() => actions.onToggleSlotLock?.(i)}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Decorative crease line at y=50%; sits in the physical device
          gap so the two halves read as paired panels rather than one
          inflated panel. pointer-events: none so it can't intercept
          clicks. */}
      <div className="sh-fold-hinge" aria-hidden />

      {/* BOTTOM HALF — "your board" (lineup row + bench + synergies) */}
      <section className="sh-fold-bottom">
        <div className="sh-fold-lineup__label tiny-cap">
          <span aria-hidden>◆</span> YOUR FOLDED FIVE
        </div>
        <div className="sh-fold-lineup__slots">
          {state.activeLine
            .map((unit, i) => ({ unit, i }))
            .reverse()
            .map(({ unit, i }) => (
              <BoardSlot
                key={i}
                unit={unit}
                index={i + 1}
                where="active"
                compact
                selected={
                  selectedBoardLoc?.kind === 'active' &&
                  selectedBoardLoc.index === i
                }
                floaters={buffFloaters.filter(
                  (f) => f.where === 'active' && f.position === i,
                )}
                onFloaterDone={onClearFloater}
                fillFx={fillFxFor('active', i)}
                onSelect={() => {
                  if (tryTapPlace({ kind: 'active', index: i })) return
                  setSelectedSlotIndex(null)
                  setSelectedBoardLoc((prev) =>
                    prev?.kind === 'active' && prev.index === i
                      ? null
                      : { kind: 'active', index: i },
                  )
                }}
              />
            ))}
        </div>
        <div className="sh-fold-rail">
          <div className="sh-fold-bench">
            <div className="sh-fold-bench__label tiny-cap">
              <span aria-hidden>⚓</span> BENCH
            </div>
            <div className="sh-fold-bench__slots">
              {state.bench.map((unit, i) => (
                <BoardSlot
                  key={i}
                  unit={unit}
                  index={i + 1}
                  where="bench"
                  compact
                  selected={
                    selectedBoardLoc?.kind === 'bench' &&
                    selectedBoardLoc.index === i
                  }
                  floaters={buffFloaters.filter(
                    (f) => f.where === 'bench' && f.position === i,
                  )}
                  onFloaterDone={onClearFloater}
                  fillFx={fillFxFor('bench', i)}
                  onSelect={() => {
                    if (tryTapPlace({ kind: 'bench', index: i })) return
                    setSelectedSlotIndex(null)
                    setSelectedBoardLoc((prev) =>
                      prev?.kind === 'bench' && prev.index === i
                        ? null
                        : { kind: 'bench', index: i },
                    )
                  }}
                />
              ))}
            </div>
          </div>
          <FoldSynergyStrip counts={synergyCounts} />
        </div>
      </section>

      <MobileTooltipTray
        selectedSlot={selectedSlot}
        selectedBoardUnit={selectedBoardUnit}
      />
      <MobileActionBar
        selectedSlot={selectedSlot}
        selectedSlotIndex={selectedSlotIndex}
        selectedBoardLoc={selectedBoardLoc}
        selectedBoardUnit={selectedBoardUnit}
        gold={state.gold}
        onBuy={actions.onBuy}
        onToggleSlotLock={actions.onToggleSlotLock}
        onSell={actions.onSell}
        clearShopSelection={() => setSelectedSlotIndex(null)}
        clearBoardSelection={() => setSelectedBoardLoc(null)}
      />
    </div>
  )
}

/** Synergy panel for the Fold bottom-half rail. The ~520px of
 *  width between the bench and the right edge comfortably hosts the
 *  full `SynergyRow` cards (same component the desktop sidebar
 *  uses) in a 2-column grid. Achieved synergies (count ≥ 2) only —
 *  inactive synergies stay off the panel so the columns read as
 *  "what you've built". */
function FoldSynergyStrip({
  counts,
}: {
  counts: SynergyCounts
}): ReactElement {
  const elements = (['fire', 'water', 'wind', 'wood', 'earth'] as const)
    .map((el) => ({ id: el, count: counts.element[el] ?? 0 }))
    .filter((x) => x.count >= 2)
  const animals = (['bird', 'mammal', 'reptile', 'fish', 'insect'] as const)
    .map((an) => ({ id: an, count: counts.animal[an] ?? 0 }))
    .filter((x) => x.count >= 2)
  const empty = elements.length === 0 && animals.length === 0
  return (
    <div className="sh-fold-synergy-strip" aria-label="active synergies">
      <div className="sh-fold-synergy-strip__label tiny-cap">
        <span aria-hidden>▰▰</span> SYNERGIES
      </div>
      {empty ? (
        <p className="sh-fold-synergy-strip__empty hand-note">none yet</p>
      ) : (
        <div className="sh-fold-synergy-strip__grid">
          {elements.map(({ id, count }) => (
            <SynergyRow key={`e-${id}`} kind="element" id={id} count={count} />
          ))}
          {animals.map(({ id, count }) => (
            <SynergyRow key={`a-${id}`} kind="animal" id={id} count={count} />
          ))}
        </div>
      )}
    </div>
  )
}

// === Synergy counting ===

interface SynergyCounts {
  element: Partial<Record<Element, number>>
  animal: Partial<Record<Animal, number>>
}

function computeSynergyCounts(state: ShopState): SynergyCounts {
  const counts: SynergyCounts = { element: {}, animal: {} }
  // Per spec §8 line 409 — "Active synergies are computed from the
  // active-line composition." Bench units are reserves and do NOT
  // contribute to synergy thresholds; only the deployed 5 do.
  const activeUnits = state.activeLine.filter(
    (u): u is BoardUnit => u !== null,
  )
  // De-duplicate by unit_id (two ★1 Crane Flocks contribute one Bird,
  // not two) but track the MAX star per unit_id — a single ★3 unit
  // contributes 2 to its element + animal so higher thresholds are
  // reachable with the 5-slot limit. Stays in lockstep with the
  // server-side `computeSynergies` in
  // packages/server/src/sim/synergies/index.ts.
  const maxStarById = new Map<
    string,
    { animal: Animal; element: Element; maxStar: Star }
  >()
  for (const u of activeUnits) {
    const id = u.unit.id
    const prev = maxStarById.get(id)
    if (!prev || u.star > prev.maxStar) {
      maxStarById.set(id, {
        animal: u.unit.animal,
        element: u.unit.element,
        maxStar: u.star,
      })
    }
  }
  for (const { animal, element, maxStar } of maxStarById.values()) {
    const c = maxStar === 3 ? 2 : 1
    counts.element[element] = (counts.element[element] ?? 0) + c
    counts.animal[animal] = (counts.animal[animal] ?? 0) + c
  }
  return counts
}

/** Small floating banner used by Shop to surface recoverable
 *  action failures ("Not enough gold...", "These pieces don't combine.").
 *  Mounted near the top of the viewport, ink-bordered with a vermillion
 *  accent so it reads as a stamped warning. Auto-dismisses after 2.6s;
 *  clicking it also dismisses. `key` on the parent forces a remount on
 *  a NEW notice so consecutive failures retrigger the animation + reset
 *  the timer. */
function ShopNoticeToast({
  message,
  onDismiss,
}: {
  message: string
  onDismiss: () => void
}): ReactElement {
  useEffect(() => {
    const h = window.setTimeout(onDismiss, 2600)
    return () => window.clearTimeout(h)
  }, [onDismiss])
  return (
    <div
      className="shop-notice-toast"
      role="alert"
      aria-live="assertive"
      onClick={onDismiss}
    >
      <span className="shop-notice-toast__kanji" aria-hidden>注</span>
      <span className="shop-notice-toast__msg">{message}</span>
    </div>
  )
}
